import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useAutoRefresh from '@/hooks/useAutoRefresh';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import StudentIdCard from '@/components/center/StudentIdCard';
import { 
  Users, Search, Plus, Edit, Trash2, Eye, Loader2, Phone, Mail,
  GraduationCap, RefreshCw, Download, MessageCircle, Filter, Upload,
  UserPlus, Megaphone, Camera, Send, Bell, IndianRupee, Calendar,
  Printer, FileText, CreditCard, Receipt, Pencil, Building2, X, MapPin,
  MoreVertical, Award, BookOpen, Lock, KeyRound, AlertCircle, CheckCircle2, Wallet
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Course categories for fee calculation - STATIC FALLBACK ONLY
// Live data is fetched dynamically from /api/courses (Single Source of Truth).
// These constants are used only if the API fetch fails.
const FALLBACK_PROFESSIONAL_COURSES = [
  // Technical Courses
  { id: 'autocad', name: 'AutoCAD', fee: 12500, category: 'professional', duration: '3 Months' },
  { id: 'civil-cad', name: 'Civil CAD', fee: 25000, category: 'professional', duration: '4 Months' },
  { id: '3ds-max', name: '3ds Max', fee: 18000, category: 'professional', duration: '3 Months' },
  { id: 'interior-arch', name: 'Interior Architecture', fee: 35000, category: 'professional', duration: '6 Months' },
  { id: 'land-surveyor', name: 'Land Surveyor', fee: 18000, category: 'professional', duration: '4 Months' },
  // IT & Digital Courses
  { id: 'digital-marketing', name: 'Digital Marketing', fee: 12000, category: 'professional', duration: '2 Months' },
  { id: 'web-designer', name: 'Web Designer', fee: 20000, category: 'professional', duration: '4 Months' },
  { id: 'data-entry', name: 'Data Entry Operator', fee: 8000, category: 'professional', duration: '2 Months' },
  // Accounting & Finance
  { id: 'tally-gst', name: 'Tally with GST', fee: 10000, category: 'professional', duration: '2 Months' },
  { id: 'financial-accounting', name: 'Financial Accounting', fee: 18500, category: 'professional', duration: '3 Months' },
  // Advanced Tech
  { id: 'ai-data-science', name: 'AI Data Science', fee: 40000, category: 'professional', duration: '6 Months' },
  { id: 'ai-data-management', name: 'AI Data Management', fee: 30000, category: 'professional', duration: '4 Months' },
  { id: 'safety-management', name: 'Safety Management', fee: 15000, category: 'professional', duration: '3 Months' },
];

// Degree courses - fee is PER YEAR (not total) - STATIC FALLBACK ONLY
const FALLBACK_DEGREE_COURSES = [
  // Diploma
  { id: 'pgdca', name: 'PGDCA', fee: 26000, category: 'diploma', type: 'Diploma', duration: 1, totalFee: 26000 },
  // Undergraduate (UG)
  { id: 'bca', name: 'BCA', fee: 21500, category: 'degree', type: 'UG', duration: 3, totalFee: 64500 },
  { id: 'bba', name: 'BBA', fee: 21500, category: 'degree', type: 'UG', duration: 3, totalFee: 64500 },
  { id: 'bcom', name: 'B.Com', fee: 21000, category: 'degree', type: 'UG', duration: 3, totalFee: 63000 },
  { id: 'ba', name: 'B.A.', fee: 16000, category: 'degree', type: 'UG', duration: 3, totalFee: 48000 },
  // Postgraduate (PG)
  { id: 'mba', name: 'MBA', fee: 35000, category: 'degree', type: 'PG', duration: 2, totalFee: 70000 },
  { id: 'mca', name: 'MCA', fee: 35000, category: 'degree', type: 'PG', duration: 2, totalFee: 70000 },
  { id: 'mcom', name: 'M.Com', fee: 21000, category: 'degree', type: 'PG', duration: 2, totalFee: 42000 },
  { id: 'msc-maths', name: 'M.Sc (Maths)', fee: 25000, category: 'degree', type: 'PG', duration: 2, totalFee: 50000 },
];

const FALLBACK_ALL_COURSES = [...FALLBACK_PROFESSIONAL_COURSES, ...FALLBACK_DEGREE_COURSES];

// Indian States and Districts
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
  "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir", "Ladakh"
];

const CenterStudentsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);

  // Dynamic course lists derived from /api/courses — Single Source of Truth.
  // Whenever Admin adds/edits/deletes a course in /admin/courses, this list reflects it.
  const PROFESSIONAL_COURSES = useMemo(() => {
    if (!courses || courses.length === 0) return FALLBACK_PROFESSIONAL_COURSES;
    return courses
      .filter((c) => c.category === 'professional' && c.is_active !== false)
      .map((c) => ({
        id: c.id,
        name: c.name,
        fee: Number(c.fee) || 0,
        category: 'professional',
        duration: c.duration || '',
      }));
  }, [courses]);

  const DEGREE_COURSES = useMemo(() => {
    if (!courses || courses.length === 0) return FALLBACK_DEGREE_COURSES;
    return courses
      .filter((c) =>
        ['degree', 'diploma', 'distance_learning', 'distance'].includes(c.category) &&
        c.is_active !== false
      )
      .map((c) => {
        const yearly = Number(c.yearly_fee || c.fee || 0);
        const years = Number(c.duration_years) || 1;
        const total = Number(c.total_fee) || yearly * years;
        const cat = c.category === 'diploma' ? 'diploma' : 'degree';
        const type =
          c.type ||
          (c.category === 'diploma'
            ? 'Diploma'
            : (c.minimum_qualification === 'graduation' ? 'PG' : 'UG'));
        return {
          id: c.id,
          name: c.name,
          fee: yearly,
          category: cat,
          type,
          duration: years,
          totalFee: total,
        };
      });
  }, [courses]);

  const ALL_COURSES = useMemo(
    () => [...PROFESSIONAL_COURSES, ...DEGREE_COURSES],
    [PROFESSIONAL_COURSES, DEGREE_COURSES]
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addLeadDialogOpen, setAddLeadDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [smsType, setSmsType] = useState('fee_reminder'); // fee_reminder, assessment_reminder
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadResults, setBulkUploadResults] = useState(null);
  const [smsForm, setSmsForm] = useState({
    pending_amount: '',
    due_date: '',
    assessment_name: '',
    assessment_date: '',
    assessment_time: ''
  });
  
  // Print dialog states
  const [printIdCardOpen, setPrintIdCardOpen] = useState(false);
  const [idCardData, setIdCardData] = useState(null);
  const [printReceiptOpen, setPrintReceiptOpen] = useState(false);
  const [printReceiptData, setPrintReceiptData] = useState(null);
  const [printFormOpen, setPrintFormOpen] = useState(false);

  // Detail panel + Collect fee modal
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsTab, setDetailsTab] = useState('overview');
  const [collectFeeOpen, setCollectFeeOpen] = useState(false);
  const [collectAmount, setCollectAmount] = useState('');
  const [collectMode, setCollectMode] = useState('Cash');
  const [collectBusy, setCollectBusy] = useState(false);
  const [centerInfo, setCenterInfo] = useState(null);
  
  // Edit and Delete states
  const [isEditing, setIsEditing] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Districts and Tahsils
  const [districts, setDistricts] = useState([]);
  const [tahsils, setTahsils] = useState([]);
  
  // Photo upload
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  
  const [studentForm, setStudentForm] = useState({
    name: '',
    father_name: '',
    mother_name: '',
    dob: '',
    gender: '',
    category: '',
    email: '',
    phone: '',
    course_id: '',
    course_name: '',
    course_category: '',
    course_duration: 0,
    course_type: '',
    training_mode: '',  // STT, RPL, or EXAM_ONLY (for distance)
    stt_delivery_mode: '', // online or on_center (only for STT)
    original_fee: 0,    // Original fee before STT/RPL calculation
    fee_amount: '',
    total_fee: '',
    fee_installment: 'one', // one or two for professional
    first_installment: '',
    second_installment: '',
    year_installments: [], // for degree/diploma courses
    address: '',
    state: '',
    district: '',
    tahsil: '',
    city: '',
    pincode: '',
    education_level: '', // 10th, 12th, ITI, Graduation, Post Graduation
    education_10th: { board: '', year: '', result: '', total_marks: '', obtained_marks: '' },
    education_12th: { board: '', year: '', result: '', total_marks: '', obtained_marks: '', stream: '' },
    education_iti: { board: '', year: '', result: '', total_marks: '', obtained_marks: '' },
    education_graduation: { university: '', degree: '', year: '', result: '', specialization: '' },
    education_pg: { university: '', degree: '', year: '', result: '', specialization: '' },
    education_graduation: { university: '', year: '', result: '' },
    education_pg: { university: '', year: '', result: '' },
    photo: ''
  });

  // Gender and Category options
  const genderOptions = ['Male', 'Female', 'Other'];
  const categoryOptions = ['General', 'OBC', 'SC', 'ST', 'EWS', 'Other'];

  const [leadForm, setLeadForm] = useState({
    name: '',
    phone: '',
    email: '',
    course: '',
    source: 'walk-in',
    notes: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchStudents();
    fetchCourses();
    fetchCenterInfo();
  }, []);

  // Auto-refresh on tab focus / regain visibility — keep student list + course
  // dropdown live with any changes made by Admin or another center session.
  // Disabled while any modal is open so the focus event after a native file
  // picker (photo upload) does not flip `loading` and unmount the dialog.
  useAutoRefresh(() => {
    fetchStudents();
    fetchCourses();
  }, { enabled: !addDialogOpen && !addLeadDialogOpen && !viewDialogOpen && !smsDialogOpen && !bulkUploadDialogOpen && !printIdCardOpen && !printReceiptOpen && !printFormOpen && !detailsPanelOpen && !collectFeeOpen && !deleteDialogOpen });

  // Fetch center info for ID card
  const fetchCenterInfo = async () => {
    try {
      const response = await axios.get(`${API}/center/me`, { headers: getAuthHeaders() });
      setCenterInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch center info:', error);
    }
  };

  // Fetch districts when state changes
  useEffect(() => {
    if (studentForm.state) {
      fetchDistricts(studentForm.state);
    } else {
      setDistricts([]);
      setTahsils([]);
    }
  }, [studentForm.state]);

  // Fetch tahsils when district changes
  useEffect(() => {
    if (studentForm.district) {
      fetchTahsils(studentForm.state, studentForm.district);
    } else {
      setTahsils([]);
    }
  }, [studentForm.district]);

  // Calculate fee when course changes
  useEffect(() => {
    if (studentForm.course_id) {
      const course = ALL_COURSES.find(c => c.id === studentForm.course_id);
      if (course) {
        // For degree/diploma courses, calculate year-wise installments
        if ((course.category === 'degree' || course.category === 'diploma') && course.duration) {
          const totalFee = course.totalFee || (course.fee * course.duration);
          const yearlyFee = course.fee;
          const yearInstallments = [];
          for (let i = 1; i <= course.duration; i++) {
            yearInstallments.push({
              year: i,
              amount: yearlyFee,
              label: `${i}${i === 1 ? 'st' : i === 2 ? 'nd' : i === 3 ? 'rd' : 'th'} Year`
            });
          }
          setStudentForm(prev => ({
            ...prev,
            course_name: course.name,
            course_category: course.category,
            course_duration: course.duration,
            course_type: course.type,
            original_fee: yearlyFee,
            fee_amount: yearlyFee,
            total_fee: totalFee,
            year_installments: yearInstallments,
            first_installment: yearlyFee,
            second_installment: 0,
            training_mode: '' // Reset training mode when course changes
          }));
        } else {
          // Professional courses - store original fee for STT/RPL calculation
          setStudentForm(prev => ({
            ...prev,
            course_name: course.name,
            course_category: course.category,
            course_duration: 0,
            course_type: '',
            original_fee: course.fee,
            fee_amount: course.fee,
            total_fee: course.fee,
            year_installments: [],
            first_installment: prev.fee_installment === 'two' 
              ? Math.round(course.fee * 0.6) : course.fee,
            second_installment: prev.fee_installment === 'two'
              ? Math.round(course.fee * 0.4) : 0,
            training_mode: '' // Reset training mode when course changes
          }));
        }
      }
    }
  }, [studentForm.course_id]);

  // Recalculate fee when training_mode changes (STT/RPL)
  useEffect(() => {
    if (studentForm.original_fee && studentForm.training_mode && studentForm.course_category === 'professional') {
      const baseFee = studentForm.training_mode === 'RPL' 
        ? Math.round(studentForm.original_fee * 0.2) 
        : studentForm.original_fee;
      
      setStudentForm(prev => ({
        ...prev,
        fee_amount: baseFee,
        total_fee: baseFee,
        first_installment: prev.fee_installment === 'two' 
          ? Math.round(baseFee * 0.6) : baseFee,
        second_installment: prev.fee_installment === 'two'
          ? Math.round(baseFee * 0.4) : 0
      }));
    }
  }, [studentForm.training_mode]);

  // Recalculate installments when fee_installment changes (Professional courses only)
  useEffect(() => {
    if (studentForm.fee_amount && studentForm.course_category === 'professional') {
      if (studentForm.fee_installment === 'two') {
        setStudentForm(prev => ({
          ...prev,
          first_installment: Math.round(prev.fee_amount * 0.6),
          second_installment: Math.round(prev.fee_amount * 0.4)
        }));
      } else {
        setStudentForm(prev => ({
          ...prev,
          first_installment: prev.fee_amount,
          second_installment: 0
        }));
      }
    }
  }, [studentForm.fee_installment, studentForm.fee_amount, studentForm.course_category]);

  const fetchStudents = async () => {
    // Only show full-page spinner on initial load; subsequent auto-refreshes
    // keep current render intact to protect open dialogs (e.g. photo upload).
    const isFirstLoad = students.length === 0;
    if (isFirstLoad) setLoading(true);
    try {
      const response = await axios.get(`${API}/center/students`, { headers: getAuthHeaders() });
      setStudents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      setStudents([]);
    } finally {
      if (isFirstLoad) setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, { headers: getAuthHeaders() });
      setCourses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch courses, using fallback list', error);
      setCourses([]);
    }
  };

  const fetchDistricts = async (state) => {
    try {
      const response = await axios.get(`${API}/districts/${encodeURIComponent(state)}`);
      setDistricts(response.data?.districts || []);
    } catch (error) {
      setDistricts([]);
    }
  };

  const fetchTahsils = async (state, district) => {
    // Mock tahsils based on district (can be replaced with real API)
    const tahsilData = {
      "Jaipur": ["Jaipur City", "Sanganer", "Amber", "Chaksu", "Bassi", "Jamwa Ramgarh", "Kotputli", "Shahpura"],
      "Alwar": ["Alwar", "Behror", "Tijara", "Mundawar", "Kishangarh Bas", "Ramgarh"],
      "Jodhpur": ["Jodhpur City", "Bilara", "Phalodi", "Osian", "Shergarh", "Bhopalgarh"],
      "Udaipur": ["Udaipur", "Vallabhnagar", "Mavli", "Girwa", "Salumber", "Kherwara"],
      "Mumbai City": ["Mumbai City"],
      "Pune": ["Haveli", "Mulshi", "Maval", "Bhor", "Velhe", "Purandar", "Baramati"],
      "Delhi": ["Central Delhi", "East Delhi", "New Delhi", "North Delhi", "South Delhi", "West Delhi"],
    };
    setTahsils(tahsilData[district] || [`${district} Tahsil`]);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 20KB)
      if (file.size > 20 * 1024) {
        toast.error('Photo size must be less than 20KB');
        return;
      }
      // Check file type
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        toast.error('Only JPG and PNG formats are allowed');
        return;
      }
      
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setStudentForm(prev => ({ ...prev, photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    
    // If editing, call update instead
    if (isEditing && editingStudentId) {
      return handleUpdateStudent(e);
    }
    
    // Validate training mode for professional courses
    const course = ALL_COURSES.find(c => c.id === studentForm.course_id);
    if (course && course.category === 'professional' && !studentForm.training_mode) {
      toast.error('Please select Training Mode (STT or RPL)');
      return;
    }
    
    // Validate STT delivery mode when STT is selected
    if (course && course.category === 'professional' && studentForm.training_mode === 'STT' && !studentForm.stt_delivery_mode) {
      toast.error('Please select Training Delivery Mode (On-Center or Online)');
      return;
    }
    
    // Calculate revenue share based on course type and training mode
    let centerShare = 40; // Default for Distance/Degree/RPL/STT Online
    let iqraShare = 60;
    
    if (course && course.category === 'professional') {
      if (studentForm.training_mode === 'STT' && studentForm.stt_delivery_mode === 'on_center') {
        centerShare = 70;
        iqraShare = 30;
      } else {
        // STT Online or RPL: 40% Center, 60% Iqra
        centerShare = 40;
        iqraShare = 60;
      }
    }
    
    // Add revenue share to form data
    const submitData = {
      ...studentForm,
      center_share_percent: centerShare,
      iqra_share_percent: iqraShare,
      enrollment_source: 'center' // Mark as center enrollment
    };
    
    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/center/students`, submitData, { headers: getAuthHeaders() });
      const { login_id, temp_password, sms_sent, email_sent } = response.data;
      
      // Show success message with credentials
      let successMessage = 'Student added successfully!';
      if (login_id && temp_password) {
        successMessage = `Student added!\n\nLogin ID: ${login_id}\nPassword: ${temp_password}`;
        if (sms_sent) successMessage += '\n\n✅ SMS sent to student';
        if (email_sent) successMessage += '\n✅ Email sent to student';
        if (!sms_sent && !email_sent) successMessage += '\n\n⚠️ Credentials not sent - please note them down';
      }
      
      toast.success(successMessage, { duration: 8000 });
      setAddDialogOpen(false);
      resetStudentForm();
      fetchStudents();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      // Handle validation errors
      if (typeof errorDetail === 'object' && Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        toast.error(`Validation Error: ${errorMessages}`);
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error('Failed to add student');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await axios.post(`${API}/center/leads`, leadForm, { headers: getAuthHeaders() });
      toast.success('Lead added successfully');
      setAddLeadDialogOpen(false);
      setLeadForm({ name: '', phone: '', email: '', course: '', source: 'walk-in', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lead');
    } finally {
      setIsSaving(false);
    }
  };

  const resetStudentForm = () => {
    setStudentForm({
      name: '', father_name: '', mother_name: '', dob: '', gender: '', category: '', email: '', phone: '',
      course_id: '', course_name: '', course_category: '', course_duration: 0, course_type: '',
      training_mode: '', stt_delivery_mode: '', original_fee: 0,
      fee_amount: '', total_fee: '', fee_installment: 'one', first_installment: '', second_installment: '',
      year_installments: [],
      address: '', state: '', district: '', tahsil: '', city: '', pincode: '',
      education_level: '',
      education_10th: { board: '', year: '', result: '', total_marks: '', obtained_marks: '' },
      education_12th: { board: '', year: '', result: '', total_marks: '', obtained_marks: '', stream: '' },
      education_iti: { board: '', year: '', result: '', total_marks: '', obtained_marks: '' },
      education_graduation: { university: '', degree: '', year: '', result: '', specialization: '' },
      education_pg: { university: '', degree: '', year: '', result: '', specialization: '' },
      photo: ''
    });
    setPhotoPreview(null);
    setPhotoFile(null);
    setIsEditing(false);
    setEditingStudentId(null);
  };

  // Edit Student Function
  const handleEditStudent = (student) => {
    setIsEditing(true);
    setEditingStudentId(student.id);
    setStudentForm({
      name: student.name || '',
      father_name: student.father_name || '',
      mother_name: student.mother_name || '',
      dob: student.dob || '',
      gender: student.gender || '',
      category: student.category || '',
      email: student.email || '',
      phone: student.mobile || student.phone || '',
      course_id: student.course_id || '',
      course_name: student.course_name || student.course || '',
      course_category: student.course_category || '',
      course_duration: student.course_duration || 0,
      course_type: student.course_type || '',
      training_mode: student.training_mode || '',
      original_fee: student.original_fee || student.course_fee || 0,
      fee_amount: student.course_fee || student.fee_amount || '',
      total_fee: student.total_fee || '',
      fee_installment: student.fee_installment || 'one',
      first_installment: student.first_installment || '',
      second_installment: student.second_installment || '',
      year_installments: student.year_installments || [],
      address: student.address || '',
      state: student.state || '',
      district: student.district || '',
      tahsil: student.tahsil || '',
      city: student.city || '',
      pincode: student.pin_code || student.pincode || '',
      education_level: student.education_level || '',
      education_10th: student.education_10th || { board: '', year: '', result: '', total_marks: '', obtained_marks: '' },
      education_12th: student.education_12th || { board: '', year: '', result: '', total_marks: '', obtained_marks: '', stream: '' },
      education_iti: student.education_iti || { board: '', year: '', result: '', total_marks: '', obtained_marks: '' },
      education_graduation: student.education_graduation || { university: '', degree: '', year: '', result: '', specialization: '' },
      education_pg: student.education_pg || { university: '', degree: '', year: '', result: '', specialization: '' },
      photo: student.photo || ''
    });
    setPhotoPreview(student.photo || null);
    setAddDialogOpen(true);
  };

  // Update Student Function
  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (!editingStudentId) return;
    
    setIsSaving(true);
    try {
      await axios.put(`${API}/center/students/${editingStudentId}`, studentForm, { headers: getAuthHeaders() });
      toast.success('Student updated successfully!');
      setAddDialogOpen(false);
      resetStudentForm();
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update student');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete Student Function
  const openDeleteDialog = (student) => {
    setStudentToDelete(student);
    setDeleteDialogOpen(true);
  };

  const handleDeleteStudent = async () => {
    if (!studentToDelete) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/center/students/${studentToDelete.id}`, { headers: getAuthHeaders() });
      toast.success(`${studentToDelete.name} deleted successfully`);
      setDeleteDialogOpen(false);
      setStudentToDelete(null);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete student');
    } finally {
      setIsDeleting(false);
    }
  };

  const openSmsDialog = (student, type) => {
    setSelectedStudent(student);
    setSmsType(type);
    setSmsForm({
      pending_amount: student.pending_fee || '',
      due_date: '',
      assessment_name: '',
      assessment_date: '',
      assessment_time: ''
    });
    setSmsDialogOpen(true);
  };

  // Print Functions
  const handlePrint = (elementId) => {
    const printContent = document.getElementById(elementId);
    if (!printContent) {
      console.error('Print content not found:', elementId);
      return;
    }
    
    // Create hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);
    
    // Determine page size and scaling based on element type
    let pageSize = 'A4 portrait';
    let pageMargin = '10mm';
    let bodyPadding = '10mm';
    let maxWidth = '190mm';
    let scale = '1';
    
    if (elementId === 'print-id-card') {
      // Single ID Card - CR80 Portrait
      pageSize = '54mm 85.6mm'; // CR80 card size
      pageMargin = '0';
      bodyPadding = '0';
      maxWidth = '54mm';
      scale = '1';
    } else if (elementId === 'print-receipt') {
      pageSize = 'A5 portrait';
      pageMargin = '8mm';
      bodyPadding = '5mm';
      maxWidth = '130mm';
    } else if (elementId === 'print-student-form') {
      // A4 FULL PAGE - No scaling, fill entire page
      pageSize = 'A4 portrait';
      pageMargin = '5mm'; // Narrow margins
      bodyPadding = '0';
      maxWidth = '200mm'; // Full width
      scale = '1'; // No scaling - full size
    }
    
    const printStyles = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&display=swap');
        
        * { 
          box-sizing: border-box; 
          margin: 0; 
          padding: 0; 
        }
        
        html {
          width: 100%;
          height: 100%;
        }
        
        body { 
          font-family: 'Inter', 'Poppins', Arial, sans-serif; 
          background: white;
          padding: ${bodyPadding};
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
          width: 100%;
          max-width: ${maxWidth};
          margin: 0 auto;
          transform: scale(${scale});
          transform-origin: top center;
        }
        
        @media print {
          html, body {
            width: 100%;
            height: auto;
            overflow: visible;
          }
          body { 
            padding: 0;
            margin: 0 auto;
            max-width: ${maxWidth};
          }
          @page { 
            size: ${pageSize}; 
            margin: ${pageMargin}; 
          }
          .no-print { display: none !important; }
        }
        
        /* Ensure images load */
        img {
          max-width: 100%;
          height: auto;
        }
        
        /* Print container */
        .print-wrapper {
          width: 100%;
          max-width: ${maxWidth};
          margin: 0 auto;
          overflow: hidden;
        }
        
        /* Print-optimized base styles */
        .print-container {
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
        }
        
        /* ID Card specific - CR80 size (54mm × 85.6mm Portrait) */
        .id-card-print {
          width: 54mm;
          height: 85.6mm;
          margin: 0 auto;
          overflow: hidden;
        }
        
        /* Receipt specific - A5 size */
        .receipt-print {
          width: 130mm;
          min-height: 180mm;
          margin: 0 auto;
          padding: 5mm;
        }
        
        /* Form specific - A4 size */
        .form-print {
          width: 190mm;
          min-height: 277mm;
          margin: 0 auto;
          padding: 5mm;
        }
        
        /* Typography */
        .text-xxs { font-size: 8px !important; }
        .text-xs { font-size: 10px !important; }
        .text-sm { font-size: 12px !important; }
        .text-base { font-size: 14px !important; }
        .text-lg { font-size: 16px !important; }
        .text-xl { font-size: 18px !important; }
        .text-2xl { font-size: 20px !important; }
        .text-3xl { font-size: 24px !important; }
        
        .font-normal { font-weight: 400 !important; }
        .font-medium { font-weight: 500 !important; }
        .font-semibold { font-weight: 600 !important; }
        .font-bold { font-weight: 700 !important; }
        
        .text-center { text-align: center !important; }
        .text-left { text-align: left !important; }
        .text-right { text-align: right !important; }
        .uppercase { text-transform: uppercase !important; }
        .tracking-wide { letter-spacing: 0.05em !important; }
        .tracking-wider { letter-spacing: 0.1em !important; }
        
        /* Colors - Print friendly (mostly black/gray) */
        .text-black { color: #000 !important; }
        .text-gray-900 { color: #111 !important; }
        .text-gray-800 { color: #1f2937 !important; }
        .text-gray-700 { color: #374151 !important; }
        .text-gray-600 { color: #4b5563 !important; }
        .text-gray-500 { color: #6b7280 !important; }
        .text-gray-400 { color: #9ca3af !important; }
        .text-white { color: #fff !important; }
        
        /* Slate colors */
        .text-slate-400 { color: #94a3b8 !important; }
        .text-slate-500 { color: #64748b !important; }
        .text-slate-600 { color: #475569 !important; }
        .text-slate-700 { color: #334155 !important; }
        .text-slate-800 { color: #1e293b !important; }
        .bg-slate-50 { background-color: #f8fafc !important; }
        .bg-slate-100 { background-color: #f1f5f9 !important; }
        .border-slate-200 { border-color: #e2e8f0 !important; }
        .border-slate-400 { border-color: #94a3b8 !important; }
        
        /* Purple/Indigo/Blue colors */
        .text-purple-400 { color: #c084fc !important; }
        .text-purple-600 { color: #9333ea !important; }
        .text-purple-700 { color: #7c3aed !important; }
        .bg-purple-50 { background-color: #faf5ff !important; }
        .bg-purple-100 { background-color: #f3e8ff !important; }
        .border-purple-200 { border-color: #e9d5ff !important; }
        .border-purple-300 { border-color: #d8b4fe !important; }
        
        /* Emerald/Teal/Cyan colors */
        .text-emerald-600 { color: #059669 !important; }
        .text-emerald-700 { color: #047857 !important; }
        .bg-emerald-50 { background-color: #ecfdf5 !important; }
        .border-emerald-200 { border-color: #a7f3d0 !important; }
        .border-emerald-500 { border-color: #10b981 !important; }
        
        /* Amber colors */
        .text-amber-600 { color: #d97706 !important; }
        .text-amber-700 { color: #b45309 !important; }
        .bg-amber-50 { background-color: #fffbeb !important; }
        .border-amber-200 { border-color: #fde68a !important; }
        .border-amber-500 { border-color: #f59e0b !important; }
        
        /* Blue colors */
        .text-blue-500 { color: #3b82f6 !important; }
        .text-blue-600 { color: #2563eb !important; }
        .bg-blue-50 { background-color: #eff6ff !important; }
        
        /* Gradients */
        .bg-gradient-to-r { background-image: linear-gradient(to right, var(--tw-gradient-stops)) !important; }
        .bg-gradient-to-br { background-image: linear-gradient(to bottom right, var(--tw-gradient-stops)) !important; }
        .from-purple-600 { --tw-gradient-from: #9333ea !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(147, 51, 234, 0)) !important; }
        .from-purple-500 { --tw-gradient-from: #a855f7 !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(168, 85, 247, 0)) !important; }
        .from-purple-50 { --tw-gradient-from: #faf5ff !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(250, 245, 255, 0)) !important; }
        .via-indigo-600 { --tw-gradient-stops: var(--tw-gradient-from), #4f46e5, var(--tw-gradient-to, rgba(79, 70, 229, 0)) !important; }
        .via-indigo-50 { --tw-gradient-stops: var(--tw-gradient-from), #eef2ff, var(--tw-gradient-to, rgba(238, 242, 255, 0)) !important; }
        .to-blue-600 { --tw-gradient-to: #2563eb !important; }
        .to-blue-50 { --tw-gradient-to: #eff6ff !important; }
        .from-emerald-600 { --tw-gradient-from: #059669 !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(5, 150, 105, 0)) !important; }
        .from-emerald-500 { --tw-gradient-from: #10b981 !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(16, 185, 129, 0)) !important; }
        .via-teal-600 { --tw-gradient-stops: var(--tw-gradient-from), #0d9488, var(--tw-gradient-to, rgba(13, 148, 136, 0)) !important; }
        .to-teal-600 { --tw-gradient-to: #0d9488 !important; }
        .to-cyan-600 { --tw-gradient-to: #0891b2 !important; }
        .from-slate-600 { --tw-gradient-from: #475569 !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(71, 85, 105, 0)) !important; }
        .to-slate-700 { --tw-gradient-to: #334155 !important; }
        
        /* Background opacity classes */
        .bg-white\\/20 { background-color: rgba(255, 255, 255, 0.2) !important; }
        .text-white\\/80 { color: rgba(255, 255, 255, 0.8) !important; }
        .text-white\\/60 { color: rgba(255, 255, 255, 0.6) !important; }
        .backdrop-blur-sm { backdrop-filter: blur(4px) !important; }
        
        .bg-black { background-color: #000 !important; }
        .bg-gray-900 { background-color: #111827 !important; }
        .bg-gray-100 { background-color: #f3f4f6 !important; }
        .bg-gray-50 { background-color: #f9fafb !important; }
        .bg-white { background-color: #fff !important; }
        
        /* Layout */
        .flex { display: flex !important; }
        .inline-flex { display: inline-flex !important; }
        .grid { display: grid !important; }
        .block { display: block !important; }
        .inline-block { display: inline-block !important; }
        .hidden { display: none !important; }
        
        .flex-col { flex-direction: column !important; }
        .flex-row { flex-direction: row !important; }
        .items-center { align-items: center !important; }
        .items-start { align-items: flex-start !important; }
        .items-end { align-items: flex-end !important; }
        .justify-center { justify-content: center !important; }
        .justify-between { justify-content: space-between !important; }
        .justify-start { justify-content: flex-start !important; }
        .justify-end { justify-content: flex-end !important; }
        
        .gap-1 { gap: 2px !important; }
        .gap-2 { gap: 4px !important; }
        .gap-3 { gap: 8px !important; }
        .gap-4 { gap: 12px !important; }
        .gap-6 { gap: 16px !important; }
        .gap-8 { gap: 24px !important; }
        
        .grid-cols-2 { grid-template-columns: repeat(2, 1fr) !important; }
        .grid-cols-3 { grid-template-columns: repeat(3, 1fr) !important; }
        .col-span-2 { grid-column: span 2 !important; }
        
        /* Spacing */
        .p-1 { padding: 2px !important; }
        .p-2 { padding: 4px !important; }
        .p-3 { padding: 8px !important; }
        .p-4 { padding: 12px !important; }
        .p-6 { padding: 16px !important; }
        .p-8 { padding: 24px !important; }
        
        .px-1 { padding-left: 2px !important; padding-right: 2px !important; }
        .px-2 { padding-left: 4px !important; padding-right: 4px !important; }
        .px-3 { padding-left: 8px !important; padding-right: 8px !important; }
        .px-4 { padding-left: 12px !important; padding-right: 12px !important; }
        .px-6 { padding-left: 16px !important; padding-right: 16px !important; }
        
        .py-1 { padding-top: 2px !important; padding-bottom: 2px !important; }
        .py-2 { padding-top: 4px !important; padding-bottom: 4px !important; }
        .py-3 { padding-top: 8px !important; padding-bottom: 8px !important; }
        .py-4 { padding-top: 12px !important; padding-bottom: 12px !important; }
        
        .pt-2 { padding-top: 4px !important; }
        .pt-4 { padding-top: 12px !important; }
        .pb-2 { padding-bottom: 4px !important; }
        .pb-4 { padding-bottom: 12px !important; }
        
        .mt-1 { margin-top: 2px !important; }
        .mt-2 { margin-top: 4px !important; }
        .mt-3 { margin-top: 8px !important; }
        .mt-4 { margin-top: 12px !important; }
        .mt-6 { margin-top: 16px !important; }
        .mt-8 { margin-top: 24px !important; }
        
        .mb-1 { margin-bottom: 2px !important; }
        .mb-2 { margin-bottom: 4px !important; }
        .mb-3 { margin-bottom: 8px !important; }
        .mb-4 { margin-bottom: 12px !important; }
        .mb-6 { margin-bottom: 16px !important; }
        
        .mx-auto { margin-left: auto !important; margin-right: auto !important; }
        
        .space-y-1 > * + * { margin-top: 2px !important; }
        .space-y-2 > * + * { margin-top: 4px !important; }
        .space-y-3 > * + * { margin-top: 8px !important; }
        .space-y-4 > * + * { margin-top: 12px !important; }
        
        /* Sizing */
        .w-full { width: 100% !important; }
        .w-auto { width: auto !important; }
        .h-auto { height: auto !important; }
        .w-8 { width: 24px !important; }
        .w-10 { width: 30px !important; }
        .w-12 { width: 36px !important; }
        .w-16 { width: 48px !important; }
        .w-20 { width: 60px !important; }
        .w-24 { width: 72px !important; }
        .h-8 { height: 24px !important; }
        .h-10 { height: 30px !important; }
        .h-12 { height: 36px !important; }
        .h-16 { height: 48px !important; }
        .h-20 { height: 60px !important; }
        .h-24 { height: 72px !important; }
        
        .min-h-16 { min-height: 48px !important; }
        
        /* Borders */
        .border { border: 1px solid #d1d5db !important; }
        .border-2 { border: 2px solid #d1d5db !important; }
        .border-t { border-top: 1px solid #d1d5db !important; }
        .border-b { border-bottom: 1px solid #d1d5db !important; }
        .border-l { border-left: 1px solid #d1d5db !important; }
        .border-r { border-right: 1px solid #d1d5db !important; }
        .border-black { border-color: #000 !important; }
        .border-gray-300 { border-color: #d1d5db !important; }
        .border-gray-400 { border-color: #9ca3af !important; }
        .border-dashed { border-style: dashed !important; }
        
        .rounded { border-radius: 4px !important; }
        .rounded-sm { border-radius: 2px !important; }
        .rounded-lg { border-radius: 8px !important; }
        .rounded-full { border-radius: 50% !important; }
        
        /* Tables */
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
        th { background-color: #f3f4f6; font-weight: 600; }
        
        /* Images */
        .object-cover { object-fit: cover !important; }
        .overflow-hidden { overflow: hidden !important; }
        
        /* Position */
        .relative { position: relative !important; }
        .absolute { position: absolute !important; }
        
        /* Misc */
        .leading-tight { line-height: 1.25 !important; }
        .leading-normal { line-height: 1.5 !important; }
        .leading-relaxed { line-height: 1.625 !important; }
        
        .truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        
        svg { display: inline-block; vertical-align: middle; width: 1em; height: 1em; }
      </style>
    `;
    
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print - Iqra Skills</title>
          ${printStyles}
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    iframeDoc.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error('Print error:', e);
      }
      // Remove iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  };

  const openPrintIdCard = async (student) => {
    setSelectedStudent(student);
    setIdCardData(null);
    setPrintIdCardOpen(true);
    try {
      const res = await axios.get(
        `${API}/center/students/${student.id}/id-card`,
        { headers: getAuthHeaders() }
      );
      setIdCardData(res.data);
    } catch (e) {
      toast.error('ID card data load nahi ho saka');
    }
  };

  const openPrintReceipt = async (student) => {
    setSelectedStudent(student);
    setPrintReceiptData(null);
    setPrintReceiptOpen(true);
    try {
      const res = await axios.get(`${API}/center/students/${student.id}/receipt-data`, { headers: getAuthHeaders() });
      setPrintReceiptData(res.data);
    } catch (e) {
      toast.error('Receipt data fetch nahi ho saka');
      // Fallback to student data
      setPrintReceiptData({
        student_id: student.student_id,
        student_name: student.name,
        phone: student.mobile || student.phone,
        course: student.course_name || student.course,
        total_fee: Number(student.total_fee || student.course_fee || 0),
        total_paid: Number(student.fee_paid || 0),
        remaining: Math.max(0, Number(student.total_fee || student.course_fee || 0) - Number(student.fee_paid || 0)),
        fee_status: student.fee_status,
        last_payment_mode: student.payment_mode || 'Cash',
      });
    }
  };

  // ============ View Details Panel ============
  const openDetailsPanel = async (student) => {
    setSelectedStudent(student);
    setDetailsData(null);
    setDetailsLoading(true);
    setDetailsTab('overview');
    setDetailsPanelOpen(true);
    try {
      const res = await axios.get(`${API}/center/students/${student.id}/details`, { headers: getAuthHeaders() });
      setDetailsData(res.data);
    } catch (e) {
      toast.error('Details fetch nahi ho saka');
    } finally {
      setDetailsLoading(false);
    }
  };

  // ============ Inline Collect Fee ============
  const openCollectFeeModal = (student) => {
    setSelectedStudent(student);
    // Default to next-due installment
    const inst1 = (Number(student.installment_1_amount) || 0) - (Number(student.installment_1_paid_amount) || 0);
    const inst2 = (Number(student.installment_2_amount) || 0) - (Number(student.installment_2_paid_amount) || 0);
    let suggested = student.fee_pending;
    if (student.installment_1_status !== 'paid' && inst1 > 0) suggested = inst1;
    else if (student.installment_2_status && student.installment_2_status !== 'paid' && student.installment_2_status !== 'na' && inst2 > 0) suggested = inst2;
    setCollectAmount(suggested?.toString() || '');
    setCollectMode('Cash');
    setCollectFeeOpen(true);
  };

  const submitCollectFee = async () => {
    if (!selectedStudent) return;
    const amount = parseFloat(collectAmount);
    if (!amount || amount <= 0) { toast.error('Please enter a valid amount'); return; }
    if (amount > selectedStudent.fee_pending) { toast.error('Amount exceeds pending fee'); return; }
    setCollectBusy(true);
    try {
      const res = await axios.post(`${API}/center/collect-fee`, {
        student_id: selectedStudent.id,
        amount,
        payment_mode: collectMode
      }, { headers: getAuthHeaders() });
      toast.success(`₹${amount.toLocaleString()} collected successfully`, {
        description: `Receipt: ${res.data.receipt_number}`
      });
      setCollectFeeOpen(false);
      // Refresh student list
      fetchStudents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to collect fee');
    } finally {
      setCollectBusy(false);
    }
  };

  // ============ Account Recovery Per-Student ============
  const handleResendCredentials = async (student) => {
    if (!window.confirm(`Resend credentials to ${student.name} via SMS?\nA new password will be generated.`)) return;
    try {
      const res = await axios.post(`${API}/center/students/${student.id}/resend-credentials`, {}, { headers: getAuthHeaders() });
      toast.success('Credentials sent via SMS', {
        description: `Login ID: ${res.data.login_id} | New Password: ${res.data.password}`,
        duration: 8000
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend');
    }
  };

  const handleForcePasswordChange = async (student) => {
    if (!window.confirm(`Force ${student.name} to change password on next login?`)) return;
    try {
      await axios.post(`${API}/center/students/${student.id}/force-password-change`, {}, { headers: getAuthHeaders() });
      toast.success('Student will be prompted to change password on next login');
    } catch (e) {
      toast.error('Failed to set flag');
    }
  };

  const openPrintForm = (student) => {
    setSelectedStudent(student);
    setPrintFormOpen(true);
  };

  const handleSendSms = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    
    setIsSaving(true);
    try {
      if (smsType === 'fee_reminder') {
        await axios.post(`${API}/center/send-fee-reminder`, {
          student_id: selectedStudent.id,
          student_name: selectedStudent.name,
          student_phone: selectedStudent.phone,
          pending_amount: parseFloat(smsForm.pending_amount),
          due_date: smsForm.due_date
        }, { headers: getAuthHeaders() });
        toast.success(`Fee reminder sent to ${selectedStudent.name}`);
      } else if (smsType === 'assessment_reminder') {
        await axios.post(`${API}/center/send-assessment-reminder`, {
          student_id: selectedStudent.id,
          student_name: selectedStudent.name,
          student_phone: selectedStudent.phone,
          assessment_name: smsForm.assessment_name,
          assessment_date: smsForm.assessment_date,
          assessment_time: smsForm.assessment_time
        }, { headers: getAuthHeaders() });
        toast.success(`Assessment reminder sent to ${selectedStudent.name}`);
      }
      setSmsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.phone?.includes(searchTerm) ||
      student.mobile?.includes(searchTerm) ||
      (student.course_name || student.course || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || student.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Export Students to Excel/CSV
  const exportStudentsToExcel = () => {
    if (students.length === 0) {
      toast.error('No students to export');
      return;
    }

    const headers = ['S.No', 'Name', 'Email', 'Mobile', 'Course', 'Total Fee', 'Paid Amount', 'Due Amount', 'Fee Status', 'Attendance %', 'Status', 'Enrollment Date'];
    
    const rows = students.map((student, index) => [
      index + 1,
      student.name || '',
      student.email || '',
      student.phone || student.mobile || '',
      student.course_name || student.course || '',
      student.total_fee || 0,
      student.paid_amount || 0,
      (student.total_fee || 0) - (student.paid_amount || 0),
      student.fee_status || 'pending',
      student.attendance || 0,
      student.status || 'active',
      student.enrollment_date || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `students_list_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Students list downloaded successfully!');
  };

  // Handle Bulk Upload
  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setBulkUploadLoading(true);
    const formData = new FormData();
    formData.append('file', bulkUploadFile);

    try {
      const token = localStorage.getItem('center_token');
      const response = await axios.post(`${API}/center/students/bulk-upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setBulkUploadResults(response.data);
      toast.success(`${response.data.success_count} students registered successfully!`);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process bulk upload');
    } finally {
      setBulkUploadLoading(false);
    }
  };

  // Download Bulk Upload Template
  const downloadBulkTemplate = () => {
    const headers = ['name', 'email', 'mobile', 'course_id', 'father_name', 'date_of_birth', 'gender', 'address'];
    const sampleRow = ['John Doe', 'john@example.com', '9876543210', 'autocad', 'Father Name', '2000-01-15', 'Male', 'Full Address'];
    
    const csvContent = [
      headers.join(','),
      sampleRow.join(','),
      '// Available course_ids: autocad, civil-cad, 3ds-max, digital-marketing, tally, etc.'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'bulk_registration_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Template downloaded!');
  };

  // Get selected course details
  const selectedCourse = ALL_COURSES.find(c => c.id === studentForm.course_id);
  const isProfessionalCourse = selectedCourse?.category === 'professional';
  const isDegreeCourse = selectedCourse?.category === 'degree' || selectedCourse?.category === 'diploma';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="center-students-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Students Management</h1>
          <p className="text-slate-500 mt-1">{students.length} total students enrolled</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchStudents} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={() => setAddLeadDialogOpen(true)} variant="outline" className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50">
            <Megaphone className="w-4 h-4" />
            Add Lead
          </Button>
          <Button onClick={() => setAddDialogOpen(true)} className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 gap-2">
            <UserPlus className="w-4 h-4" />
            Add Student
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-xl font-bold text-slate-900">{students.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Active</p>
              <p className="text-xl font-bold text-slate-900">{students.filter(s => s.status === 'active').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Warning</p>
              <p className="text-xl font-bold text-slate-900">{students.filter(s => s.status === 'warning').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Fee Due</p>
              <p className="text-xl font-bold text-slate-900">{students.filter(s => s.fee_status === 'due').length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, phone, email, course..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg text-sm bg-white"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="warning">Warning</option>
          <option value="dropout">Dropout</option>
        </select>
        
        {/* Download Excel Button */}
        <Button 
          variant="outline" 
          className="gap-2"
          onClick={() => exportStudentsToExcel()}
        >
          <Download className="w-4 h-4" />
          Download Excel
        </Button>
        
        {/* Bulk Upload Button */}
        <Button 
          variant="outline" 
          className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
          onClick={() => setBulkUploadDialogOpen(true)}
        >
          <Upload className="w-4 h-4" />
          Bulk Registration
        </Button>
      </div>

      {/* Table - Responsive with Horizontal Scroll */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div 
          className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e1 #f1f5f9'
          }}
          data-testid="students-management-table-container"
        >
        <table className="w-full min-w-[900px]">
          <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600 whitespace-nowrap min-w-[200px]">Student</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600 whitespace-nowrap min-w-[150px]">Father Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600 whitespace-nowrap min-w-[140px]">Mobile</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600 whitespace-nowrap min-w-[140px]">Course</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600 whitespace-nowrap min-w-[200px]">Fees</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600 whitespace-nowrap w-[60px]">⋮</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredStudents.map((student) => {
              const feeStatus = student.fee_status || (student.fee_pending > 0 ? (student.fee_paid > 0 ? 'partial' : 'due') : 'paid');
              return (
              <tr key={student.id} className="hover:bg-slate-50 transition-colors" data-testid={`student-row-${student.id}`}>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-3 cursor-pointer" onClick={() => openDetailsPanel(student)}>
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold">{student.name?.charAt(0)}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate max-w-[160px] hover:text-purple-700">{student.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{student.student_id}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">{student.father_name || '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1 text-sm text-slate-600">
                    <Phone className="w-3 h-3 flex-shrink-0" />
                    {student.phone || student.mobile || 'N/A'}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium truncate max-w-[120px] inline-block">
                    {student.course_name || student.course || 'N/A'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {feeStatus === 'paid' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openPrintReceipt(student)}
                      className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1"
                      data-testid={`view-receipt-${student.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3" /> View Receipt
                    </Button>
                  ) : feeStatus === 'partial' ? (
                    <Button
                      size="sm"
                      onClick={() => openCollectFeeModal(student)}
                      className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white gap-1"
                      data-testid={`pay-balance-${student.id}`}
                    >
                      <Wallet className="w-3 h-3" /> Pay Balance · ₹{(student.fee_pending || 0).toLocaleString()}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => openCollectFeeModal(student)}
                      className="h-7 text-xs bg-rose-500 hover:bg-rose-600 text-white gap-1"
                      data-testid={`collect-fee-${student.id}`}
                    >
                      <IndianRupee className="w-3 h-3" /> Collect Fees · ₹{(student.fee_pending || student.course_fee || 0).toLocaleString()}
                    </Button>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100" data-testid={`more-${student.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {/* PRIMARY */}
                      <DropdownMenuLabel className="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">Primary</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => openDetailsPanel(student)} data-testid={`menu-view-details-${student.id}`}>
                        <Eye className="w-4 h-4 mr-2 text-purple-600" /> View Details
                      </DropdownMenuItem>

                      {/* FINANCE */}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">Finance</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => openCollectFeeModal(student)} disabled={feeStatus === 'paid'}>
                        <IndianRupee className="w-4 h-4 mr-2 text-emerald-600" /> Collect Fees
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openPrintReceipt(student)}>
                        <Receipt className="w-4 h-4 mr-2 text-emerald-600" /> Print Receipt
                      </DropdownMenuItem>

                      {/* DOCUMENTS */}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">Documents</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => openPrintIdCard(student)}>
                        <CreditCard className="w-4 h-4 mr-2 text-purple-600" /> Print ID Card
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/center/certificates')} disabled={!student.exam_passed && student.fee_status !== 'paid'}>
                        <Award className="w-4 h-4 mr-2 text-amber-600" /> Certificate / Marksheet
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openPrintForm(student)}>
                        <FileText className="w-4 h-4 mr-2 text-slate-600" /> Print Student Form
                      </DropdownMenuItem>

                      {/* ACCOUNT */}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">Account</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleResendCredentials(student)}>
                        <KeyRound className="w-4 h-4 mr-2 text-blue-600" /> Resend Credentials (SMS)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleForcePasswordChange(student)}>
                        <Lock className="w-4 h-4 mr-2 text-orange-600" /> Force Password Change
                      </DropdownMenuItem>

                      {/* MANAGEMENT */}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">Management</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleEditStudent(student)}>
                        <Pencil className="w-4 h-4 mr-2 text-blue-600" /> Edit Student
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openDeleteDialog(student)} className="text-red-600 focus:text-red-700">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete Student
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {/* Mobile Scroll Hint */}
        <div className="lg:hidden px-4 py-2 text-center text-xs text-slate-400 border-t border-slate-100">
          ← Scroll horizontally to view all columns →
        </div>
      </div>

      {/* Add/Edit Student Dialog - Enhanced */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => { if (!open) resetStudentForm(); setAddDialogOpen(open); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isEditing ? <Pencil className="w-5 h-5 text-blue-600" /> : <UserPlus className="w-5 h-5 text-purple-600" />}
              {isEditing ? 'Edit Student / छात्र संपादित करें' : 'Add New Student / नया छात्र जोड़ें'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddStudent} className="space-y-6">
            {/* Photo Upload */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 border-4 border-white shadow-lg flex items-center justify-center overflow-hidden">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-8 h-8 text-purple-400" />
                  )}
                </div>
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-purple-700 transition-colors">
                  <Upload className="w-4 h-4 text-white" />
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
            <p className="text-center text-xs text-slate-500">Photo Upload (Max 20KB, JPG/PNG)</p>

            {/* Personal Details */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-4">
              <h4 className="font-semibold text-slate-700 text-sm">Personal Details / व्यक्तिगत विवरण</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Full Name / पूरा नाम *</Label>
                  <Input
                    value={studentForm.name}
                    onChange={(e) => setStudentForm({...studentForm, name: e.target.value})}
                    placeholder="Enter full name"
                    required
                  />
                </div>
                <div>
                  <Label>Father's Name / पिता का नाम *</Label>
                  <Input
                    value={studentForm.father_name}
                    onChange={(e) => setStudentForm({...studentForm, father_name: e.target.value})}
                    placeholder="Enter father's name"
                    required
                  />
                </div>
                <div>
                  <Label>Mother's Name / माता का नाम *</Label>
                  <Input
                    value={studentForm.mother_name}
                    onChange={(e) => setStudentForm({...studentForm, mother_name: e.target.value})}
                    placeholder="Enter mother's name"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Date of Birth / जन्म तिथि *</Label>
                  <Input
                    type="date"
                    value={studentForm.dob}
                    onChange={(e) => setStudentForm({...studentForm, dob: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Gender / लिंग *</Label>
                  <select
                    value={studentForm.gender}
                    onChange={(e) => setStudentForm({...studentForm, gender: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    required
                  >
                    <option value="">Select Gender</option>
                    {genderOptions.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Category / श्रेणी *</Label>
                  <select
                    value={studentForm.category}
                    onChange={(e) => setStudentForm({...studentForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    required
                  >
                    <option value="">Select Category</option>
                    {categoryOptions.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Phone / मोबाइल *</Label>
                  <Input
                    value={studentForm.phone}
                    onChange={(e) => setStudentForm({...studentForm, phone: e.target.value})}
                    placeholder="9876543210"
                    required
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={studentForm.email}
                    onChange={(e) => setStudentForm({...studentForm, email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Address Details */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-4">
              <h4 className="font-semibold text-slate-700 text-sm">Address / पता</h4>
              <div>
                <Label>Address / पता</Label>
                <Input
                  value={studentForm.address}
                  onChange={(e) => setStudentForm({...studentForm, address: e.target.value})}
                  placeholder="House No., Street, Locality"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>State / राज्य *</Label>
                  <select
                    value={studentForm.state}
                    onChange={(e) => setStudentForm({...studentForm, state: e.target.value, district: '', tahsil: ''})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    required
                  >
                    <option value="">Select State</option>
                    {INDIAN_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>District / जिला *</Label>
                  <select
                    value={studentForm.district}
                    onChange={(e) => setStudentForm({...studentForm, district: e.target.value, tahsil: ''})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    required
                    disabled={!studentForm.state}
                  >
                    <option value="">Select District</option>
                    {districts.map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Tahsil/Taluka / तहसील</Label>
                  <select
                    value={studentForm.tahsil}
                    onChange={(e) => setStudentForm({...studentForm, tahsil: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    disabled={!studentForm.district}
                  >
                    <option value="">Select Tahsil</option>
                    {tahsils.map(tahsil => (
                      <option key={tahsil} value={tahsil}>{tahsil}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>City / शहर</Label>
                  <Input
                    value={studentForm.city}
                    onChange={(e) => setStudentForm({...studentForm, city: e.target.value})}
                    placeholder="City/Village"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pin Code / पिन कोड *</Label>
                  <Input
                    value={studentForm.pincode}
                    onChange={(e) => setStudentForm({...studentForm, pincode: e.target.value})}
                    placeholder="302001"
                    maxLength={6}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Course & Fee Details */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-4">
              <h4 className="font-semibold text-slate-700 text-sm">Course & Fee Details / कोर्स और फीस</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Course / कोर्स *</Label>
                  <select
                    value={studentForm.course_id}
                    onChange={(e) => setStudentForm({...studentForm, course_id: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    required
                  >
                    <option value="">Select Course</option>
                    <optgroup label="Professional Courses (STT: On-Center 70%/30%, Online 40%/60% | RPL: 40%/60%)">
                      {PROFESSIONAL_COURSES.map(course => (
                        <option key={course.id} value={course.id}>{course.name} - ₹{course.fee.toLocaleString()} ({course.duration})</option>
                      ))}
                    </optgroup>
                    <optgroup label="Distance/Degree Courses (Center 40% : IQRA 60%)">
                      {DEGREE_COURSES.map(course => (
                        <option key={course.id} value={course.id}>{course.name} ({course.type}) - ₹{course.fee.toLocaleString()}/year x {course.duration} years</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                
                {/* STT/RPL Mode Selection - Only for Professional Courses */}
                {isProfessionalCourse && studentForm.course_id && (
                  <div className="md:col-span-2">
                    <Label className="mb-2 block">Training Mode / प्रशिक्षण मोड *</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div 
                        onClick={() => setStudentForm({...studentForm, training_mode: 'STT', stt_delivery_mode: ''})}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          studentForm.training_mode === 'STT'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            studentForm.training_mode === 'STT' ? 'border-blue-500' : 'border-slate-300'
                          }`}>
                            {studentForm.training_mode === 'STT' && (
                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            )}
                          </div>
                          <span className={`font-bold ${studentForm.training_mode === 'STT' ? 'text-blue-700' : 'text-slate-700'}`}>
                            STT - Full Training
                          </span>
                        </div>
                        <p className="text-lg font-bold text-blue-600 ml-6">₹{studentForm.original_fee?.toLocaleString()}</p>
                        <p className="text-xs text-slate-500 ml-6">Complete training with certification</p>
                      </div>
                      <div 
                        onClick={() => setStudentForm({...studentForm, training_mode: 'RPL', stt_delivery_mode: ''})}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          studentForm.training_mode === 'RPL'
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-slate-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            studentForm.training_mode === 'RPL' ? 'border-purple-500' : 'border-slate-300'
                          }`}>
                            {studentForm.training_mode === 'RPL' && (
                              <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                            )}
                          </div>
                          <span className={`font-bold ${studentForm.training_mode === 'RPL' ? 'text-purple-700' : 'text-slate-700'}`}>
                            RPL - Assessment Only
                          </span>
                        </div>
                        <p className="text-lg font-bold text-purple-600 ml-6">₹{Math.round(studentForm.original_fee * 0.2)?.toLocaleString()}</p>
                        <p className="text-xs text-purple-600 ml-6">Direct exam, no training (20% fee)</p>
                        <p className="text-xs text-amber-600 ml-6 mt-1">Revenue: 40% Center | 60% Iqra</p>
                      </div>
                    </div>
                    {!studentForm.training_mode && (
                      <p className="text-xs text-amber-600 mt-2">* Please select STT or RPL mode</p>
                    )}
                  </div>
                )}

                {/* STT Delivery Mode - Only when STT is selected */}
                {isProfessionalCourse && studentForm.training_mode === 'STT' && (
                  <div className="md:col-span-2">
                    <Label className="mb-2 block">Training Delivery Mode / प्रशिक्षण विधि *</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div 
                        onClick={() => setStudentForm({...studentForm, stt_delivery_mode: 'on_center'})}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          studentForm.stt_delivery_mode === 'on_center'
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-slate-200 hover:border-emerald-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            studentForm.stt_delivery_mode === 'on_center' ? 'border-emerald-500' : 'border-slate-300'
                          }`}>
                            {studentForm.stt_delivery_mode === 'on_center' && (
                              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                            )}
                          </div>
                          <span className={`font-bold ${studentForm.stt_delivery_mode === 'on_center' ? 'text-emerald-700' : 'text-slate-700'}`}>
                            🏫 On-Center Training
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 ml-6">Student trains at your center</p>
                        <p className="text-xs text-emerald-600 ml-6 mt-1 font-semibold">Revenue: 70% Center | 30% Iqra</p>
                      </div>
                      <div 
                        onClick={() => setStudentForm({...studentForm, stt_delivery_mode: 'online'})}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          studentForm.stt_delivery_mode === 'online'
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-slate-200 hover:border-cyan-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            studentForm.stt_delivery_mode === 'online' ? 'border-cyan-500' : 'border-slate-300'
                          }`}>
                            {studentForm.stt_delivery_mode === 'online' && (
                              <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                            )}
                          </div>
                          <span className={`font-bold ${studentForm.stt_delivery_mode === 'online' ? 'text-cyan-700' : 'text-slate-700'}`}>
                            💻 Online Training
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 ml-6">Student trains online via LMS</p>
                        <p className="text-xs text-cyan-600 ml-6 mt-1 font-semibold">Revenue: 40% Center | 60% Iqra</p>
                      </div>
                    </div>
                    {!studentForm.stt_delivery_mode && (
                      <p className="text-xs text-amber-600 mt-2">* Please select training delivery mode</p>
                    )}
                  </div>
                )}

                {/* Distance/Degree Course Info - Exam Only Mode */}
                {isDegreeCourse && studentForm.course_id && (
                  <div className="md:col-span-2 bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-cyan-700 font-bold">📘 Distance Education / Degree Program</span>
                    </div>
                    <p className="text-sm text-cyan-800">Mode: <strong>Exam Only</strong> (No on-site training required)</p>
                    <p className="text-xs text-cyan-600 mt-1">Revenue Sharing: <strong>40% Center | 60% Iqra Skills</strong></p>
                  </div>
                )}
                
                <div>
                  <Label>{isProfessionalCourse ? 'Total Fees / कुल फीस' : 'Per Year Fee / प्रति वर्ष फीस'}</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <Input
                      type="number"
                      value={studentForm.fee_amount}
                      className="pl-8"
                      readOnly
                    />
                  </div>
                  {isDegreeCourse && (
                    <p className="text-xs text-blue-600 mt-1">* Per Year Fee shown. Total: ₹{studentForm.total_fee?.toLocaleString()} ({studentForm.course_duration} years). Revenue: 40% Center | 60% IQRA</p>
                  )}
                  {isProfessionalCourse && studentForm.training_mode === 'STT' && studentForm.stt_delivery_mode === 'on_center' && (
                    <p className="text-xs text-emerald-600 mt-1">* STT On-Center Training. Revenue: 70% Center | 30% IQRA</p>
                  )}
                  {isProfessionalCourse && studentForm.training_mode === 'STT' && studentForm.stt_delivery_mode === 'online' && (
                    <p className="text-xs text-cyan-600 mt-1">* STT Online Training. Revenue: 40% Center | 60% IQRA</p>
                  )}
                  {isProfessionalCourse && studentForm.training_mode === 'RPL' && (
                    <p className="text-xs text-purple-600 mt-1">* RPL Assessment Only (20% of course fee). Revenue: 40% Center | 60% IQRA</p>
                  )}
                </div>
              </div>

              {/* Fee Installment - Only for Professional Courses */}
              {isProfessionalCourse && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <Label className="mb-3 block">Fee Installment / किस्त विकल्प</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="fee_installment"
                        value="one"
                        checked={studentForm.fee_installment === 'one'}
                        onChange={(e) => setStudentForm({...studentForm, fee_installment: e.target.value})}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-sm">One Installment (Full Payment)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="fee_installment"
                        value="two"
                        checked={studentForm.fee_installment === 'two'}
                        onChange={(e) => setStudentForm({...studentForm, fee_installment: e.target.value})}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-sm">Two Installments (60% + 40%)</span>
                    </label>
                  </div>
                  {studentForm.fee_installment === 'two' && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                        <p className="text-xs text-emerald-600 mb-1">1st Installment (60%)</p>
                        <p className="text-lg font-bold text-emerald-700">₹{studentForm.first_installment?.toLocaleString()}</p>
                      </div>
                      <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                        <p className="text-xs text-amber-600 mb-1">2nd Installment (40%)</p>
                        <p className="text-lg font-bold text-amber-700">₹{studentForm.second_installment?.toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Year-wise Installments - For Degree/Diploma Courses */}
              {isDegreeCourse && studentForm.year_installments.length > 0 && (
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-blue-700">Year-wise Fee Structure / वर्षवार शुल्क</Label>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      {studentForm.course_duration} Year {studentForm.course_type}
                    </span>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 mb-3 border border-blue-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-blue-600">Total Course Fee / कुल कोर्स फीस</span>
                      <span className="text-xl font-bold text-blue-700">₹{studentForm.total_fee?.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className={`grid gap-3 ${studentForm.year_installments.length <= 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    {studentForm.year_installments.map((installment, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${
                          idx === 0 ? 'bg-emerald-50 border-emerald-200' : 
                          idx === 1 ? 'bg-amber-50 border-amber-200' : 
                          'bg-purple-50 border-purple-200'
                        }`}
                      >
                        <p className={`text-xs mb-1 ${
                          idx === 0 ? 'text-emerald-600' : 
                          idx === 1 ? 'text-amber-600' : 
                          'text-purple-600'
                        }`}>
                          {installment.label} / {idx + 1}वां वर्ष
                        </p>
                        <p className={`text-lg font-bold ${
                          idx === 0 ? 'text-emerald-700' : 
                          idx === 1 ? 'text-amber-700' : 
                          'text-purple-700'
                        }`}>
                          ₹{installment.amount?.toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    * Each year's fee will be collected separately. Revenue: 40% Center | 60% IQRA
                  </p>
                </div>
              )}
            </div>

            {/* Education Qualification */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-4">
              <h4 className="font-semibold text-slate-700 text-sm">Education Qualification / शैक्षिक योग्यता</h4>
              <div>
                <Label>Highest Qualification / उच्चतम योग्यता *</Label>
                <select
                  value={studentForm.education_level}
                  onChange={(e) => setStudentForm({...studentForm, education_level: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  required
                >
                  <option value="">Select Qualification</option>
                  <option value="10th">10th / दसवीं</option>
                  <option value="12th">12th / बारहवीं</option>
                  <option value="ITI">ITI / आईटीआई</option>
                  <option value="Graduation">Graduation / स्नातक</option>
                  <option value="Post Graduation">Post Graduation / स्नातकोत्तर</option>
                </select>
              </div>

              {/* 10th Details - Always show if education_level is 10th or higher */}
              {studentForm.education_level && (
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <h5 className="text-sm font-medium text-slate-700 mb-3">10th Details / दसवीं विवरण</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Board / बोर्ड</Label>
                      <Input
                        value={studentForm.education_10th.board}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_10th: {...studentForm.education_10th, board: e.target.value}
                        })}
                        placeholder="e.g., RBSE, CBSE"
                      />
                    </div>
                    <div>
                      <Label>Passing Year / उत्तीर्ण वर्ष</Label>
                      <Input
                        value={studentForm.education_10th.year}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_10th: {...studentForm.education_10th, year: e.target.value}
                        })}
                        placeholder="2020"
                        maxLength={4}
                      />
                    </div>
                    <div>
                      <Label>Percentage / प्रतिशत</Label>
                      <Input
                        value={studentForm.education_10th.result}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_10th: {...studentForm.education_10th, result: e.target.value}
                        })}
                        placeholder="e.g., 85 (only number)"
                      />
                    </div>
                    <div>
                      <Label>Total Marks / कुल अंक</Label>
                      <Input
                        value={studentForm.education_10th.total_marks}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_10th: {...studentForm.education_10th, total_marks: e.target.value}
                        })}
                        placeholder="e.g., 500"
                      />
                    </div>
                    <div>
                      <Label>Obtained Marks / प्राप्त अंक</Label>
                      <Input
                        value={studentForm.education_10th.obtained_marks}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_10th: {...studentForm.education_10th, obtained_marks: e.target.value}
                        })}
                        placeholder="e.g., 425"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 12th Details */}
              {['12th', 'ITI', 'Graduation', 'Post Graduation'].includes(studentForm.education_level) && (
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <h5 className="text-sm font-medium text-slate-700 mb-3">12th Details / बारहवीं विवरण</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Board / बोर्ड</Label>
                      <Input
                        value={studentForm.education_12th.board}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_12th: {...studentForm.education_12th, board: e.target.value}
                        })}
                        placeholder="e.g., RBSE, CBSE"
                      />
                    </div>
                    <div>
                      <Label>Stream / स्ट्रीम</Label>
                      <Input
                        value={studentForm.education_12th.stream}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_12th: {...studentForm.education_12th, stream: e.target.value}
                        })}
                        placeholder="Science / Commerce / Arts"
                      />
                    </div>
                    <div>
                      <Label>Passing Year / उत्तीर्ण वर्ष</Label>
                      <Input
                        value={studentForm.education_12th.year}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_12th: {...studentForm.education_12th, year: e.target.value}
                        })}
                        placeholder="2022"
                        maxLength={4}
                      />
                    </div>
                    <div>
                      <Label>Percentage / प्रतिशत</Label>
                      <Input
                        value={studentForm.education_12th.result}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_12th: {...studentForm.education_12th, result: e.target.value}
                        })}
                        placeholder="e.g., 75"
                      />
                    </div>
                    <div>
                      <Label>Total Marks</Label>
                      <Input
                        value={studentForm.education_12th.total_marks}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_12th: {...studentForm.education_12th, total_marks: e.target.value}
                        })}
                        placeholder="e.g., 500"
                      />
                    </div>
                    <div>
                      <Label>Obtained Marks</Label>
                      <Input
                        value={studentForm.education_12th.obtained_marks}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_12th: {...studentForm.education_12th, obtained_marks: e.target.value}
                        })}
                        placeholder="e.g., 380"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ITI Details */}
              {studentForm.education_level === 'ITI' && (
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <h5 className="text-sm font-medium text-slate-700 mb-3">ITI Details / आईटीआई विवरण</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Trade / ट्रेड</Label>
                      <Input
                        value={studentForm.education_iti.board}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_iti: {...studentForm.education_iti, board: e.target.value}
                        })}
                        placeholder="e.g., Electrician, Fitter"
                      />
                    </div>
                    <div>
                      <Label>Passing Year / उत्तीर्ण वर्ष</Label>
                      <Input
                        value={studentForm.education_iti.year}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_iti: {...studentForm.education_iti, year: e.target.value}
                        })}
                        placeholder="2023"
                        maxLength={4}
                      />
                    </div>
                    <div>
                      <Label>Percentage / Grade</Label>
                      <Input
                        value={studentForm.education_iti.result}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_iti: {...studentForm.education_iti, result: e.target.value}
                        })}
                        placeholder="e.g., 78 or Distinction"
                      />
                    </div>
                    <div>
                      <Label>Total Marks</Label>
                      <Input
                        value={studentForm.education_iti.total_marks}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_iti: {...studentForm.education_iti, total_marks: e.target.value}
                        })}
                        placeholder="e.g., 800"
                      />
                    </div>
                    <div>
                      <Label>Obtained Marks</Label>
                      <Input
                        value={studentForm.education_iti.obtained_marks}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_iti: {...studentForm.education_iti, obtained_marks: e.target.value}
                        })}
                        placeholder="e.g., 620"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Graduation Details */}
              {['Graduation', 'Post Graduation'].includes(studentForm.education_level) && (
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <h5 className="text-sm font-medium text-slate-700 mb-3">Graduation Details / स्नातक विवरण</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>University / विश्वविद्यालय</Label>
                      <Input
                        value={studentForm.education_graduation.university}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_graduation: {...studentForm.education_graduation, university: e.target.value}
                        })}
                        placeholder="e.g., Rajasthan University"
                      />
                    </div>
                    <div>
                      <Label>Passing Year / उत्तीर्ण वर्ष</Label>
                      <Input
                        value={studentForm.education_graduation.year}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_graduation: {...studentForm.education_graduation, year: e.target.value}
                        })}
                        placeholder="2024"
                        maxLength={4}
                      />
                    </div>
                    <div>
                      <Label>Result/Grade / परिणाम</Label>
                      <Input
                        value={studentForm.education_graduation.result}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_graduation: {...studentForm.education_graduation, result: e.target.value}
                        })}
                        placeholder="e.g., 65% or First Division"
                      />
                    </div>
                    <div>
                      <Label>Course / Degree</Label>
                      <Input
                        value={studentForm.education_graduation.degree}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_graduation: {...studentForm.education_graduation, degree: e.target.value}
                        })}
                        placeholder="e.g., B.Tech, B.Com, BA"
                      />
                    </div>
                    <div>
                      <Label>Specialization</Label>
                      <Input
                        value={studentForm.education_graduation.specialization}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_graduation: {...studentForm.education_graduation, specialization: e.target.value}
                        })}
                        placeholder="e.g., Computer Science"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Post Graduation Details */}
              {studentForm.education_level === 'Post Graduation' && (
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <h5 className="text-sm font-medium text-slate-700 mb-3">Post Graduation Details / स्नातकोत्तर विवरण</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>University / विश्वविद्यालय</Label>
                      <Input
                        value={studentForm.education_pg.university}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_pg: {...studentForm.education_pg, university: e.target.value}
                        })}
                        placeholder="e.g., Rajasthan University"
                      />
                    </div>
                    <div>
                      <Label>Passing Year / उत्तीर्ण वर्ष</Label>
                      <Input
                        value={studentForm.education_pg.year}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_pg: {...studentForm.education_pg, year: e.target.value}
                        })}
                        placeholder="2025"
                        maxLength={4}
                      />
                    </div>
                    <div>
                      <Label>Result/Grade / परिणाम</Label>
                      <Input
                        value={studentForm.education_pg.result}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_pg: {...studentForm.education_pg, result: e.target.value}
                        })}
                        placeholder="e.g., 70% or First Division"
                      />
                    </div>
                    <div>
                      <Label>Course / Degree</Label>
                      <Input
                        value={studentForm.education_pg.degree}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_pg: {...studentForm.education_pg, degree: e.target.value}
                        })}
                        placeholder="e.g., M.Tech, MBA, MA"
                      />
                    </div>
                    <div>
                      <Label>Specialization</Label>
                      <Input
                        value={studentForm.education_pg.specialization}
                        onChange={(e) => setStudentForm({
                          ...studentForm,
                          education_pg: {...studentForm.education_pg, specialization: e.target.value}
                        })}
                        placeholder="e.g., Computer Science"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => { setAddDialogOpen(false); resetStudentForm(); }}>Cancel</Button>
              <Button type="submit" disabled={isSaving} className="bg-gradient-to-r from-purple-600 to-blue-600">
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Add Student
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={addLeadDialogOpen} onOpenChange={setAddLeadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-purple-600" />
              Add New Lead / नया लीड जोड़ें
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLead} className="space-y-4">
            <div>
              <Label>Name / नाम *</Label>
              <Input
                value={leadForm.name}
                onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                placeholder="Enter name"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone / मोबाइल *</Label>
                <Input
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                  placeholder="9876543210"
                  required
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Course Interest / कोर्स</Label>
                <select
                  value={leadForm.course}
                  onChange={(e) => setLeadForm({...leadForm, course: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="">Select Course</option>
                  {ALL_COURSES.map(course => (
                    <option key={course.id} value={course.name}>{course.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Source / स्रोत</Label>
                <select
                  value={leadForm.source}
                  onChange={(e) => setLeadForm({...leadForm, source: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="walk-in">Walk-in</option>
                  <option value="phone">Phone Inquiry</option>
                  <option value="website">Website</option>
                  <option value="social-media">Social Media</option>
                  <option value="referral">Referral</option>
                  <option value="newspaper">Newspaper Ad</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Notes / नोट्स</Label>
              <Input
                value={leadForm.notes}
                onChange={(e) => setLeadForm({...leadForm, notes: e.target.value})}
                placeholder="Any additional notes..."
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setAddLeadDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={isSaving} className="bg-gradient-to-r from-purple-600 to-blue-600">
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Lead
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Student Details</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">{selectedStudent.name?.charAt(0)}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedStudent.name}</h3>
                  <p className="text-slate-500">{selectedStudent.course}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedStudent.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">{selectedStudent.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Fee Status</p>
                  <p className="font-medium capitalize">{selectedStudent.fee_status}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Attendance</p>
                  <p className="font-medium">{selectedStudent.attendance}%</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SMS Notification Dialog */}
      <Dialog open={smsDialogOpen} onOpenChange={setSmsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {smsType === 'fee_reminder' ? (
                <>
                  <IndianRupee className="w-5 h-5 text-amber-600" />
                  Fee Reminder SMS / फीस रिमाइंडर
                </>
              ) : (
                <>
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Assessment Reminder SMS / परीक्षा रिमाइंडर
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <form onSubmit={handleSendSms} className="space-y-4">
              {/* Student Info */}
              <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">{selectedStudent.name?.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{selectedStudent.name}</p>
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {selectedStudent.phone}
                  </p>
                </div>
              </div>

              {smsType === 'fee_reminder' ? (
                <>
                  <div>
                    <Label>Pending Amount / बकाया राशि *</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                      <Input
                        type="number"
                        value={smsForm.pending_amount}
                        onChange={(e) => setSmsForm({...smsForm, pending_amount: e.target.value})}
                        placeholder="5000"
                        className="pl-8"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Due Date / अंतिम तिथि</Label>
                    <Input
                      type="date"
                      value={smsForm.due_date}
                      onChange={(e) => setSmsForm({...smsForm, due_date: e.target.value})}
                    />
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                    <p className="text-xs text-amber-600 mb-1">SMS Preview / संदेश पूर्वावलोकन:</p>
                    <p className="text-sm text-amber-800">
                      "Dear {selectedStudent.name}, your fee of ₹{smsForm.pending_amount || '0'} is pending. 
                      Please pay by {smsForm.due_date || 'earliest'}. - Iqra Skills"
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>Assessment Name / परीक्षा का नाम *</Label>
                    <Input
                      value={smsForm.assessment_name}
                      onChange={(e) => setSmsForm({...smsForm, assessment_name: e.target.value})}
                      placeholder="e.g., AutoCAD Final Exam"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date / तारीख *</Label>
                      <Input
                        type="date"
                        value={smsForm.assessment_date}
                        onChange={(e) => setSmsForm({...smsForm, assessment_date: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Time / समय</Label>
                      <Input
                        type="time"
                        value={smsForm.assessment_time}
                        onChange={(e) => setSmsForm({...smsForm, assessment_time: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-xs text-blue-600 mb-1">SMS Preview / संदेश पूर्वावलोकन:</p>
                    <p className="text-sm text-blue-800">
                      "Dear {selectedStudent.name}, your {smsForm.assessment_name || 'assessment'} is scheduled on{' '}
                      {smsForm.assessment_date || 'TBD'} at {smsForm.assessment_time || '10:00 AM'}. 
                      All the best! - Iqra Skills"
                    </p>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setSmsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSaving}
                  className={smsType === 'fee_reminder' 
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' 
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                  }
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send SMS
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Print ID Card Dialog — uses new single-source-of-truth StudentIdCard */}
      <Dialog open={printIdCardOpen} onOpenChange={setPrintIdCardOpen}>
        <DialogContent className="max-w-md max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-orange-500" />
              Student ID Card / स्टूडेंट आईडी कार्ड
            </DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div className="flex justify-center p-4 bg-slate-100 rounded-lg overflow-hidden">
                <div style={{ width: '54mm', height: '85.6mm', transform: 'scale(1.6)', transformOrigin: 'top center' }}>
                  {idCardData ? (
                    <StudentIdCard data={idCardData} scale={1} />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full text-slate-400 text-xs">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-slate-500 text-center">
                CR80 Card Size: 54mm × 85.6mm — Validity auto-calculated from course duration.
              </p>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setPrintIdCardOpen(false)} className="gap-2" data-testid="student-idcard-cancel-btn">
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
                <Button
                  onClick={() => window.open(`/print/student-id/${selectedStudent.id}`, '_blank')}
                  disabled={!idCardData}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 gap-2 shadow-lg shadow-orange-500/25"
                  data-testid="student-idcard-print-btn"
                >
                  <Printer className="w-4 h-4" />
                  Print ID Card
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Receipt Dialog - Standard Size with Logo Left */}
      <Dialog open={printReceiptOpen} onOpenChange={setPrintReceiptOpen}>
        <DialogContent className="max-w-xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" />
              Print Payment Receipt / भुगतान रसीद प्रिंट
            </DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              {/* Standard Receipt Preview with WYSIWYG Layout */}
              <div id="print-receipt" className="bg-slate-100 p-4 rounded-lg">
                <div style={{ 
                  width: '100%', 
                  maxWidth: '420px', 
                  fontFamily: 'Inter, Arial, sans-serif',
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  margin: '0 auto',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact'
                }}>
                  
                  {/* Header: Logo Left + Center Name */}
                  <div style={{ 
                    background: 'linear-gradient(to right, #059669, #0d9488, #0891b2)',
                    color: 'white',
                    padding: '16px',
                    position: 'relative',
                    overflow: 'hidden',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}>
                    {/* Background Pattern */}
                    <div style={{ 
                      position: 'absolute', 
                      inset: 0, 
                      opacity: 0.1,
                      backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                      backgroundSize: '10px 10px'
                    }}></div>
                    
                    <div style={{ position: 'relative', zIndex: 10 }}>
                      {/* Logo Left + Center Name Center */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        {/* Logo - Left Side - BIGGER */}
                        <img 
                          src="/iqraskills-logo.png" 
                          alt="Logo" 
                          style={{ 
                            width: '80px', 
                            height: '80px', 
                            borderRadius: '10px',
                            backgroundColor: 'white',
                            padding: '6px',
                            objectFit: 'contain',
                            flexShrink: 0
                          }} 
                        />
                        
                        {/* Center Name & Address - Center */}
                        <div style={{ flex: 1, textAlign: 'center' }}>
                          <h1 style={{ fontSize: '20px', fontWeight: '800', letterSpacing: '0.05em', margin: 0 }}>
                            {centerInfo?.center_name || 'Training Center'}
                          </h1>
                          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', marginTop: '4px' }}>
                            {centerInfo?.address ? `${centerInfo.address}, ${centerInfo.city || ''}, ${centerInfo.district || ''}` : 'Center Address'}
                          </p>
                          <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace', marginTop: '2px' }}>
                            Code: {centerInfo?.center_code || 'IQRA-XX-00000'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Receipt Title Badge */}
                      <div style={{ marginTop: '12px', textAlign: 'center' }}>
                        <div style={{ 
                          display: 'inline-block',
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          padding: '8px 16px'
                        }}>
                          <p style={{ fontSize: '14px', fontWeight: 'bold', letterSpacing: '0.1em', margin: 0 }}>PAYMENT RECEIPT</p>
                          <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)', margin: '2px 0 0' }}>भुगतान रसीद</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info Banner */}
                  <div style={{ 
                    backgroundColor: '#ecfdf5',
                    borderBottom: '1px solid #d1fae5',
                    padding: '8px 16px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '24px',
                    fontSize: '12px',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Phone className="w-3 h-3 text-emerald-600" />
                      <span style={{ color: '#047857', fontWeight: '500' }}>{centerInfo?.mobile || centerInfo?.phone || 'Contact'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Mail className="w-3 h-3 text-emerald-600" />
                      <span style={{ color: '#047857', fontWeight: '500' }}>{centerInfo?.email || 'Email'}</span>
                    </div>
                  </div>

                  {/* Receipt Details */}
                  <div className="p-4 space-y-4">
                    
                    {/* Receipt Info Row */}
                    <div className="flex justify-between text-sm border-b border-dashed border-slate-300 pb-3">
                      <div>
                        <p className="text-slate-400 text-xs">Receipt No.</p>
                        <p className="font-bold font-mono text-slate-800">{printReceiptData?.last_receipt_no || `RCP-${(selectedStudent.student_id || 'TMP').slice(-6)}`}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-400 text-xs">Date</p>
                        <p className="font-semibold text-slate-800">{printReceiptData?.last_payment_date ? new Date(printReceiptData.last_payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                      </div>
                    </div>

                    {/* Student Info */}
                    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-slate-400 text-xs uppercase">Student Name</p>
                          <p className="font-bold text-slate-800">{printReceiptData?.student_name || selectedStudent.name}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs uppercase">Student ID</p>
                          <p className="font-mono font-semibold text-emerald-700">{printReceiptData?.student_id || selectedStudent.student_id || 'STU-' + (selectedStudent.id?.slice(0,6) || '000000').toUpperCase()}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs uppercase">Mobile</p>
                          <p className="font-medium text-slate-700">{printReceiptData?.phone || selectedStudent.phone || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs uppercase">Course</p>
                          <p className="font-medium text-sm text-slate-700">{printReceiptData?.course || selectedStudent.course_name || selectedStudent.course || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Table - UNIFIED LOGIC */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left px-3 py-2 text-slate-600 font-semibold">Description</th>
                            <th className="text-right px-3 py-2 text-slate-600 font-semibold w-28">Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <p className="font-medium text-slate-700">Total Course Fee</p>
                              <p className="text-xs text-slate-500">{printReceiptData?.course || selectedStudent.course_name || selectedStudent.course}</p>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-700">
                              {(printReceiptData?.total_fee || 0).toLocaleString()}
                            </td>
                          </tr>
                          <tr className="border-t border-slate-100 bg-emerald-50">
                            <td className="px-3 py-2 text-emerald-700">
                              <p className="font-medium">Total Paid</p>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                              {(printReceiptData?.total_paid || 0).toLocaleString()}
                            </td>
                          </tr>
                          {(printReceiptData?.remaining || 0) > 0 && (
                            <tr className="bg-rose-50 border-t border-slate-100">
                              <td className="px-3 py-2 text-rose-700">
                                <p className="font-medium">Remaining Balance</p>
                              </td>
                              <td className="px-3 py-2 text-right font-semibold text-rose-700">
                                {(printReceiptData?.remaining || 0).toLocaleString()}
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          <tr className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                            <td className="px-3 py-3 font-bold text-base">
                              TOTAL PAID
                            </td>
                            <td className="px-3 py-3 text-right font-bold text-xl">
                              ₹{(printReceiptData?.total_paid || 0).toLocaleString()}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Payment Status Badge - only if PAID IN FULL */}
                    {(printReceiptData?.remaining || 0) <= 0 && (printReceiptData?.total_paid || 0) > 0 && (
                      <div className="text-center">
                        <span className="inline-block px-4 py-2 rounded-lg border-2 font-bold text-sm uppercase tracking-wider border-emerald-500 text-emerald-600 bg-emerald-50">
                          ✓ PAID IN FULL
                        </span>
                      </div>
                    )}

                    {/* Payment Mode */}
                    <div className="flex justify-between text-sm border-t border-dashed border-slate-300 pt-3">
                      <div>
                        <p className="text-slate-400 text-xs uppercase">Payment Mode</p>
                        <p className="font-semibold text-slate-700">{printReceiptData?.last_payment_mode || 'Cash'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-400 text-xs uppercase">Transaction ID</p>
                        <p className="font-mono text-xs text-slate-600">{printReceiptData?.last_transaction_id ? printReceiptData.last_transaction_id.slice(0, 14).toUpperCase() : '—'}</p>
                      </div>
                    </div>

                    {/* Signatures */}
                    <div className="flex justify-between pt-4 mt-4 border-t border-slate-200">
                      <div className="text-center">
                        <div className="w-24 h-10 border-b-2 border-slate-400 mb-1"></div>
                        <p className="text-xs text-slate-600">Student Sign</p>
                      </div>
                      <div className="text-center">
                        <div className="w-24 h-10 border-b-2 border-slate-400 mb-1"></div>
                        <p className="text-xs text-slate-600">Authorized Sign</p>
                      </div>
                    </div>

                    {/* Footer Note */}
                    <div className="text-center pt-3 border-t border-slate-100">
                      <p className="text-[9px] text-slate-400">
                        * This is a computer generated receipt.
                      </p>
                      <p className="text-[9px] text-slate-400">
                        यह एक कंप्यूटर जनित रसीद है।
                      </p>
                    </div>
                  </div>
                  
                  {/* Bottom Gradient Strip */}
                  <div className="h-1.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600"></div>
                </div>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Print Size: A5 (148mm × 210mm)
              </p>
              
              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setPrintReceiptOpen(false)} className="gap-2">
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
                <Button 
                  onClick={() => handlePrint('print-receipt')} 
                  className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-700 hover:via-teal-700 hover:to-cyan-700 gap-2 shadow-lg shadow-emerald-500/25"
                >
                  <Printer className="w-4 h-4" />
                  Print Receipt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Student Form Dialog - A4 with Logo & Center Details */}
      <Dialog open={printFormOpen} onOpenChange={setPrintFormOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Print Student Form / छात्र फॉर्म प्रिंट
            </DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              {/* A4 Form Preview - FULL PAGE with INLINE STYLES for Print */}
              <div id="print-student-form" className="bg-slate-100 p-2 rounded-lg overflow-auto">
                <div style={{ 
                  width: '100%', 
                  maxWidth: '800px', 
                  fontFamily: 'Inter, Arial, sans-serif', 
                  fontSize: '13px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  margin: '0 auto',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact'
                }}>
                  
                  {/* Header - Logo Left, Center Name Center - INLINE GRADIENT */}
                  <div style={{ 
                    background: 'linear-gradient(to right, #9333ea, #4f46e5, #2563eb)',
                    color: 'white',
                    padding: '20px',
                    position: 'relative',
                    overflow: 'hidden',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}>
                    {/* Background Pattern */}
                    <div style={{ 
                      position: 'absolute', 
                      inset: 0, 
                      opacity: 0.1,
                      backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                      backgroundSize: '12px 12px'
                    }}></div>
                    
                    <div style={{ position: 'relative', zIndex: 10 }}>
                      {/* Logo Left + Center Name Center Layout */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                        {/* Logo - Left Side - DOUBLE SIZE */}
                        <img 
                          src="/iqraskills-logo.png" 
                          alt="Logo" 
                          style={{ 
                            width: '128px', 
                            height: '128px', 
                            borderRadius: '12px',
                            backgroundColor: 'white',
                            padding: '8px',
                            objectFit: 'contain',
                            flexShrink: 0
                          }} 
                        />
                        
                        {/* Center Name & Address - Center */}
                        <div style={{ flex: 1, textAlign: 'center' }}>
                          <h1 style={{ fontSize: '28px', fontWeight: '800', letterSpacing: '0.05em', margin: 0 }}>
                            {centerInfo?.center_name || 'Training Center'}
                          </h1>
                          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginTop: '4px' }}>
                            {centerInfo?.address ? `${centerInfo.address}, ${centerInfo.city || ''}, ${centerInfo.district || ''}, ${centerInfo.state || ''}` : 'Center Address'}
                          </p>
                          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', fontFamily: 'monospace', marginTop: '2px' }}>
                            Code: {centerInfo?.center_code || 'IQRA-XX-00000'} | Ph: {centerInfo?.mobile || 'Contact'}
                          </p>
                          
                          {/* Registration Form Title */}
                          <div style={{ marginTop: '12px' }}>
                            <div style={{ 
                              display: 'inline-block',
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              borderRadius: '8px',
                              padding: '8px 24px'
                            }}>
                              <p style={{ fontSize: '18px', fontWeight: 'bold', letterSpacing: '0.1em', margin: 0 }}>STUDENT REGISTRATION FORM</p>
                              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.85)', margin: '2px 0 0' }}>छात्र पंजीकरण फॉर्म</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Form No - Right */}
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', margin: 0 }}>Form No.</p>
                          <p style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '14px', margin: 0 }}>REG-{Date.now().toString().slice(-8)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Academic Year & Date Banner */}
                  <div style={{ 
                    backgroundColor: '#faf5ff',
                    borderBottom: '1px solid #f3e8ff',
                    padding: '8px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                      <div>
                        <p style={{ fontSize: '10px', color: '#9333ea', textTransform: 'uppercase', fontWeight: '600', margin: 0 }}>Academic Year</p>
                        <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b', margin: '2px 0 0' }}>2026-27</p>
                      </div>
                      <div>
                        <p style={{ fontSize: '10px', color: '#9333ea', textTransform: 'uppercase', fontWeight: '600', margin: 0 }}>Registration Date</p>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#334155', margin: '2px 0 0' }}>{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MapPin className="w-4 h-4 text-purple-600" />
                      <span style={{ fontSize: '14px', color: '#7c3aed', fontWeight: '500' }}>{centerInfo?.district || 'District'}, {centerInfo?.state || 'State'}</span>
                    </div>
                  </div>

                  {/* Main Content - Full A4 with INLINE STYLES */}
                  <div style={{ padding: '24px' }}>
                    
                    {/* Top Row: Photo + Basic Info */}
                    <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                      {/* Photo Box */}
                      <div style={{ flexShrink: 0 }}>
                        <div style={{ 
                          width: '96px', 
                          height: '128px', 
                          border: '2px dashed #d8b4fe',
                          background: 'linear-gradient(to bottom right, #faf5ff, #eff6ff)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          WebkitPrintColorAdjust: 'exact',
                          printColorAdjust: 'exact'
                        }}>
                          {(selectedStudent.photo || selectedStudent.photo_url) ? (
                            <img src={selectedStudent.photo || selectedStudent.photo_url} alt="Photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ textAlign: 'center', color: '#c084fc' }}>
                              <Camera className="w-8 h-8 mx-auto mb-1" />
                              <p style={{ fontSize: '9px' }}>Photo</p>
                            </div>
                          )}
                        </div>
                        <p style={{ fontSize: '9px', textAlign: 'center', color: '#94a3b8', marginTop: '4px' }}>3.5×4.5cm</p>
                      </div>
                      
                      {/* Student ID Box */}
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          padding: '16px',
                          background: 'linear-gradient(to bottom right, #faf5ff, #eef2ff)',
                          height: '100%',
                          WebkitPrintColorAdjust: 'exact',
                          printColorAdjust: 'exact'
                        }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                              <p style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', margin: 0 }}>Student ID</p>
                              <p style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '18px', color: '#7c3aed', margin: '4px 0 0' }}>{selectedStudent.student_id || 'STU-' + (selectedStudent.id?.slice(0,8) || '00000000').toUpperCase()}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', margin: 0 }}>Enrollment Date</p>
                              <p style={{ fontWeight: '600', fontSize: '16px', color: '#334155', margin: '4px 0 0' }}>{selectedStudent.enrollment_date ? new Date(selectedStudent.enrollment_date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', margin: 0 }}>Center Code</p>
                              <p style={{ fontFamily: 'monospace', fontWeight: '600', fontSize: '16px', color: '#9333ea', margin: '4px 0 0' }}>{centerInfo?.center_code || 'N/A'}</p>
                            </div>
                            <div>
                              <p style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', margin: 0 }}>Center Name</p>
                              <p style={{ fontWeight: '600', fontSize: '14px', color: '#334155', margin: '4px 0 0' }}>{selectedStudent.center_name || centerInfo?.center_name || 'Training Center'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section 1: Personal Details - INLINE STYLES */}
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                      <div style={{ 
                        background: 'linear-gradient(to right, #a855f7, #9333ea)',
                        color: 'white',
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        WebkitPrintColorAdjust: 'exact',
                        printColorAdjust: 'exact'
                      }}>
                        <Users className="w-4 h-4" />
                        <h3 style={{ fontWeight: 'bold', fontSize: '14px', margin: 0 }}>1. PERSONAL DETAILS / व्यक्तिगत विवरण</h3>
                      </div>
                      <div style={{ padding: '16px', backgroundColor: 'white' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Full Name / पूरा नाम</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.name || '_________________'}</p>
                          </div>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Father's Name / पिता का नाम</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.father_name || '_________________'}</p>
                          </div>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Mother's Name / माता का नाम</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.mother_name || '_________________'}</p>
                          </div>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Date of Birth / जन्म तिथि</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.dob || selectedStudent.date_of_birth || '_________________'}</p>
                          </div>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Gender / लिंग</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.gender || '_________________'}</p>
                          </div>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Category / श्रेणी</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.category || 'General'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Contact Details - INLINE STYLES */}
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                      <div style={{ 
                        background: 'linear-gradient(to right, #3b82f6, #2563eb)',
                        color: 'white',
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        WebkitPrintColorAdjust: 'exact',
                        printColorAdjust: 'exact'
                      }}>
                        <Phone className="w-4 h-4" />
                        <h3 style={{ fontWeight: 'bold', fontSize: '14px', margin: 0 }}>2. CONTACT DETAILS / संपर्क विवरण</h3>
                      </div>
                      <div style={{ padding: '16px', backgroundColor: 'white' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Mobile Number / मोबाइल नंबर</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.phone || selectedStudent.mobile || '_________________'}</p>
                          </div>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Email / ईमेल</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.email || '_________________'}</p>
                          </div>
                        </div>
                        <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginTop: '12px' }}>
                          <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Full Address / पूरा पता</p>
                          <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.address || '_________________________________________'}</p>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '12px' }}>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>State / राज्य</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.state || '_________'}</p>
                          </div>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>District / जिला</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.district || '_________'}</p>
                          </div>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Pin Code</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.pin_code || selectedStudent.pincode || '_________'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section 3: Course & Fee Details - INLINE STYLES */}
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                      <div style={{ 
                        background: 'linear-gradient(to right, #10b981, #059669)',
                        color: 'white',
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        WebkitPrintColorAdjust: 'exact',
                        printColorAdjust: 'exact'
                      }}>
                        <GraduationCap className="w-4 h-4" />
                        <h3 style={{ fontWeight: 'bold', fontSize: '14px', margin: 0 }}>3. COURSE & FEE DETAILS / कोर्स एवं शुल्क विवरण</h3>
                      </div>
                      <div style={{ padding: '16px', backgroundColor: 'white' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Course Name / कोर्स का नाम</p>
                            <p style={{ fontWeight: 'bold', fontSize: '14px', color: '#047857', margin: '4px 0 0' }}>{selectedStudent.course_name || selectedStudent.course || '_________________'}</p>
                          </div>
                          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Duration / अवधि</p>
                            <p style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b', margin: '4px 0 0' }}>{selectedStudent.course_duration || '6 Months'}</p>
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '16px' }}>
                          <div style={{ 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '8px', 
                            padding: '12px', 
                            textAlign: 'center', 
                            backgroundColor: '#f8fafc',
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact'
                          }}>
                            <p style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Total Fee</p>
                            <p style={{ fontWeight: 'bold', fontSize: '20px', color: '#1e293b', margin: '4px 0 0' }}>₹{(selectedStudent.total_fee || 0).toLocaleString()}</p>
                          </div>
                          <div style={{ 
                            border: '1px solid #a7f3d0', 
                            borderRadius: '8px', 
                            padding: '12px', 
                            textAlign: 'center', 
                            backgroundColor: '#ecfdf5',
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact'
                          }}>
                            <p style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Paid</p>
                            <p style={{ fontWeight: 'bold', fontSize: '20px', color: '#059669', margin: '4px 0 0' }}>₹{(selectedStudent.paid_amount || 0).toLocaleString()}</p>
                          </div>
                          <div style={{ 
                            border: '1px solid #fde68a', 
                            borderRadius: '8px', 
                            padding: '12px', 
                            textAlign: 'center', 
                            backgroundColor: '#fffbeb',
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact'
                          }}>
                            <p style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', margin: 0 }}>Balance</p>
                            <p style={{ fontWeight: 'bold', fontSize: '20px', color: '#d97706', margin: '4px 0 0' }}>₹{((selectedStudent.total_fee || 0) - (selectedStudent.paid_amount || 0)).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section 4: Declaration - INLINE STYLES */}
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                      <div style={{ 
                        background: 'linear-gradient(to right, #475569, #334155)',
                        color: 'white',
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        WebkitPrintColorAdjust: 'exact',
                        printColorAdjust: 'exact'
                      }}>
                        <FileText className="w-4 h-4" />
                        <h3 style={{ fontWeight: 'bold', fontSize: '14px', margin: 0 }}>4. DECLARATION / घोषणा</h3>
                      </div>
                      <div style={{ padding: '16px', backgroundColor: '#f8fafc', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <div style={{ 
                            width: '16px', 
                            height: '16px', 
                            border: '1px solid #94a3b8', 
                            borderRadius: '4px', 
                            marginTop: '2px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            flexShrink: 0 
                          }}>
                            <div style={{ width: '8px', height: '8px', backgroundColor: '#475569', borderRadius: '2px' }}></div>
                          </div>
                          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
                            <p style={{ margin: 0 }}>
                              I hereby declare that all the information provided above is true and correct. 
                              I agree to abide by the rules and regulations of the Training Center.
                              / मैं घोषणा करता/करती हूं कि उपरोक्त जानकारी सत्य है। मैं प्रशिक्षण केंद्र के नियमों का पालन करने के लिए सहमत हूं।
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Signatures - INLINE STYLES */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginTop: '24px', paddingTop: '16px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ height: '48px', borderBottom: '2px solid #94a3b8', marginBottom: '8px' }}></div>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#334155', margin: 0 }}>Student Signature</p>
                        <p style={{ fontSize: '10px', color: '#64748b', margin: '2px 0 0' }}>छात्र हस्ताक्षर</p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ height: '48px', borderBottom: '2px solid #94a3b8', marginBottom: '8px' }}></div>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#334155', margin: 0 }}>Parent/Guardian</p>
                        <p style={{ fontSize: '10px', color: '#64748b', margin: '2px 0 0' }}>अभिभावक</p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ height: '48px', borderBottom: '2px solid #94a3b8', marginBottom: '8px' }}></div>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#334155', margin: 0 }}>Center Incharge</p>
                        <p style={{ fontSize: '10px', color: '#64748b', margin: '2px 0 0' }}>केंद्र प्रभारी</p>
                      </div>
                    </div>

                    {/* Footer - INLINE STYLES */}
                    <div style={{ marginTop: '16px', paddingTop: '8px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#94a3b8' }}>
                      <p style={{ margin: 0 }}>Date: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                      <p style={{ margin: 0 }}>Computer generated form</p>
                    </div>
                  </div>
                  
                  {/* Bottom Gradient Strip - INLINE STYLES */}
                  <div style={{ 
                    height: '8px', 
                    background: 'linear-gradient(to right, #9333ea, #4f46e5, #2563eb)',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact'
                  }}></div>
                </div>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Print Size: A4 (210mm × 297mm) - Full Page
              </p>
              
              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setPrintFormOpen(false)} className="gap-2">
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
                <Button 
                  onClick={() => handlePrint('print-student-form')} 
                  className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 hover:from-purple-700 hover:via-indigo-700 hover:to-blue-700 gap-2 shadow-lg shadow-purple-500/25"
                >
                  <Printer className="w-4 h-4" />
                  Print Form
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Student / छात्र हटाएं
            </DialogTitle>
          </DialogHeader>
          {studentToDelete && (
            <div className="space-y-4">
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-red-800">
                  <strong>Warning:</strong> This action cannot be undone. Are you sure you want to delete this student?
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-lg">
                    {studentToDelete.name?.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{studentToDelete.name}</h4>
                    <p className="text-sm text-slate-500">ID: {studentToDelete.student_id}</p>
                    <p className="text-sm text-slate-500">Course: {studentToDelete.course_name || studentToDelete.course}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setStudentToDelete(null); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteStudent}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white gap-2"
                >
                  {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete Student
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadDialogOpen} onOpenChange={setBulkUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Upload className="w-5 h-5" />
              Bulk Student Registration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-medium text-purple-800 mb-2">Instructions:</h4>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>1. Download the template CSV file</li>
                <li>2. Fill in student details (name, email, mobile, course_id)</li>
                <li>3. Upload the filled CSV file</li>
                <li>4. System will auto-generate login credentials and send via email</li>
              </ul>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full gap-2"
              onClick={downloadBulkTemplate}
            >
              <Download className="w-4 h-4" />
              Download Template CSV
            </Button>
            
            <div className="border-t pt-4">
              <Label>Upload Filled CSV File</Label>
              <Input
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => setBulkUploadFile(e.target.files[0])}
                className="mt-2"
              />
              {bulkUploadFile && (
                <p className="text-sm text-slate-500 mt-1">Selected: {bulkUploadFile.name}</p>
              )}
            </div>
            
            {bulkUploadResults && (
              <div className="bg-slate-50 border rounded-lg p-4">
                <h4 className="font-medium text-slate-800 mb-2">Upload Results:</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-emerald-100 text-emerald-700 rounded p-2 text-center">
                    <p className="text-lg font-bold">{bulkUploadResults.success_count}</p>
                    <p>Successful</p>
                  </div>
                  <div className="bg-red-100 text-red-700 rounded p-2 text-center">
                    <p className="text-lg font-bold">{bulkUploadResults.failed_count}</p>
                    <p>Failed</p>
                  </div>
                </div>
                {bulkUploadResults.errors?.length > 0 && (
                  <div className="mt-3 text-xs text-red-600 max-h-24 overflow-y-auto">
                    {bulkUploadResults.errors.map((err, i) => (
                      <p key={i}>Row {err.row}: {err.error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => { setBulkUploadDialogOpen(false); setBulkUploadFile(null); setBulkUploadResults(null); }}>
                Close
              </Button>
              <Button 
                onClick={handleBulkUpload}
                disabled={!bulkUploadFile || bulkUploadLoading}
                className="bg-purple-600 hover:bg-purple-700 gap-2"
              >
                {bulkUploadLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload & Register
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ View Details Side Panel ============ */}
      <Sheet open={detailsPanelOpen} onOpenChange={setDetailsPanelOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col overflow-hidden">
          <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-purple-50 via-blue-50 to-cyan-50">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-xl">
                {selectedStudent?.name?.charAt(0)}
              </div>
              <div className="flex-1 text-left">
                <SheetTitle className="text-lg">{selectedStudent?.name}</SheetTitle>
                <SheetDescription className="font-mono text-xs">{selectedStudent?.student_id} · {selectedStudent?.course_name}</SheetDescription>
              </div>
            </div>
            <div className="flex gap-1 mt-3 border-b -mb-4 -mx-6 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: Eye },
                { id: 'fees', label: 'Fees', icon: IndianRupee },
                { id: 'attendance', label: 'Attendance', icon: Calendar },
                { id: 'documents', label: 'Documents', icon: FileText },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setDetailsTab(t.id)}
                  className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition-colors ${detailsTab === t.id ? 'border-purple-600 text-purple-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                  data-testid={`tab-${t.id}`}
                >
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6">
            {detailsLoading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-500" />
                <p className="text-sm text-slate-500 mt-2">Loading student details...</p>
              </div>
            ) : !detailsData ? (
              <p className="text-sm text-slate-500 text-center py-10">No data</p>
            ) : detailsTab === 'overview' ? (
              <div className="space-y-3">
                {[
                  ['Father Name', detailsData.student.father_name],
                  ['Mobile', detailsData.student.mobile || detailsData.student.phone],
                  ['Email', detailsData.student.email],
                  ['DOB', detailsData.student.dob],
                  ['Course', detailsData.student.course_name],
                  ['Training Mode', `${detailsData.student.training_mode || '—'} · ${detailsData.student.stt_delivery_mode || '—'}`],
                  ['Enrollment Date', detailsData.student.enrollment_date ? new Date(detailsData.student.enrollment_date).toLocaleDateString('en-IN') : '—'],
                  ['Address', `${detailsData.student.address || ''}, ${detailsData.student.city || ''}, ${detailsData.student.state || ''} - ${detailsData.student.pin_code || ''}`],
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
                    <span className="text-slate-500 font-medium">{label}</span>
                    <span className="text-slate-800 font-semibold text-right max-w-[60%]">{val || '—'}</span>
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
                    <p className="text-[10px] text-emerald-600 uppercase font-bold">Attendance</p>
                    <p className="text-2xl font-extrabold text-emerald-700">{detailsData.attendance.percentage}%</p>
                    <p className="text-[10px] text-slate-500">{detailsData.attendance.present}/{detailsData.attendance.total} classes</p>
                  </div>
                  <div className={`rounded-xl p-3 text-center border ${detailsData.is_passed ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100'}`}>
                    <p className={`text-[10px] uppercase font-bold ${detailsData.is_passed ? 'text-amber-600' : 'text-slate-500'}`}>Exam Status</p>
                    <p className={`text-2xl font-extrabold ${detailsData.is_passed ? 'text-amber-700' : 'text-slate-500'}`}>{detailsData.is_passed ? 'Passed' : 'Pending'}</p>
                    <p className="text-[10px] text-slate-500">{detailsData.exam_results.length} exam{detailsData.exam_results.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
            ) : detailsTab === 'fees' ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div className="bg-slate-50 rounded-lg p-2.5 text-center"><p className="text-[10px] text-slate-500 uppercase">Total</p><p className="font-bold text-slate-900">₹{(detailsData.student.total_fee || detailsData.student.course_fee || 0).toLocaleString()}</p></div>
                  <div className="bg-emerald-50 rounded-lg p-2.5 text-center"><p className="text-[10px] text-emerald-600 uppercase">Paid</p><p className="font-bold text-emerald-700">₹{(detailsData.student.fee_paid || 0).toLocaleString()}</p></div>
                  <div className="bg-rose-50 rounded-lg p-2.5 text-center"><p className="text-[10px] text-rose-600 uppercase">Pending</p><p className="font-bold text-rose-700">₹{(detailsData.student.fee_pending || 0).toLocaleString()}</p></div>
                </div>
                {[1, 2].map(n => {
                  const amt = Number(detailsData.student[`installment_${n}_amount`]) || 0;
                  if (amt <= 0) return null;
                  const paid = Number(detailsData.student[`installment_${n}_paid_amount`]) || 0;
                  const status = detailsData.student[`installment_${n}_status`] || 'pending';
                  return (
                    <div key={n} className={`border rounded-xl p-3 ${status === 'paid' ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-slate-200'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-sm">{n === 1 ? '1st' : '2nd'} Installment</p>
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${status === 'paid' ? 'bg-emerald-100 text-emerald-700' : status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>{status}</span>
                      </div>
                      <p className="text-xs text-slate-600">₹{amt.toLocaleString()} · Paid ₹{paid.toLocaleString()}</p>
                    </div>
                  );
                })}
                <div>
                  <h5 className="text-xs font-bold uppercase text-slate-500 mt-4 mb-2">Recent Transactions</h5>
                  {detailsData.fee_transactions.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No transactions yet</p>
                  ) : detailsData.fee_transactions.slice(0, 5).map(t => (
                    <div key={t.id} className="flex justify-between items-center py-2 border-b border-slate-100 text-sm">
                      <div>
                        <p className="font-mono text-xs text-slate-700">{t.receipt_number}</p>
                        <p className="text-[10px] text-slate-400">{t.payment_date ? new Date(t.payment_date).toLocaleDateString('en-IN') : ''} · {t.payment_mode}</p>
                      </div>
                      <span className="font-bold text-emerald-600">₹{(t.amount || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : detailsTab === 'attendance' ? (
              <div className="text-center py-10 space-y-3">
                <div className="text-7xl font-extrabold text-emerald-600">{detailsData.attendance.percentage}%</div>
                <p className="text-sm text-slate-600">{detailsData.attendance.present} present out of {detailsData.attendance.total} classes</p>
                <Button onClick={() => navigate('/center/attendance')} variant="outline" className="rounded-lg">
                  <Calendar className="w-4 h-4 mr-2" /> View Full Attendance
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <Button onClick={() => openPrintIdCard(selectedStudent)} variant="outline" className="w-full justify-start rounded-lg h-11">
                  <CreditCard className="w-4 h-4 mr-2 text-purple-600" /> Print ID Card
                </Button>
                <Button onClick={() => openPrintReceipt(selectedStudent)} variant="outline" className="w-full justify-start rounded-lg h-11">
                  <Receipt className="w-4 h-4 mr-2 text-emerald-600" /> Print Receipt
                </Button>
                <Button onClick={() => openPrintForm(selectedStudent)} variant="outline" className="w-full justify-start rounded-lg h-11">
                  <FileText className="w-4 h-4 mr-2 text-slate-600" /> Print Student Form
                </Button>
                <Button
                  onClick={() => navigate('/center/certificates')}
                  variant="outline"
                  className="w-full justify-start rounded-lg h-11"
                  disabled={!detailsData.is_passed}
                  title={!detailsData.is_passed ? 'Available after passing exam' : ''}
                >
                  <Award className="w-4 h-4 mr-2 text-amber-600" /> Certificate / Marksheet
                  {!detailsData.is_passed && <span className="ml-auto text-[10px] text-slate-400">Locked</span>}
                </Button>
              </div>
            )}
          </div>

          {/* Footer Quick Actions */}
          {detailsData && (
            <div className="border-t bg-white p-3 flex flex-wrap gap-2">
              {(detailsData.student.fee_pending || 0) > 0 && (
                <Button size="sm" onClick={() => { setDetailsPanelOpen(false); openCollectFeeModal(detailsData.student); }} className="bg-emerald-600 hover:bg-emerald-700 flex-1 min-w-[120px]">
                  <IndianRupee className="w-4 h-4 mr-1.5" /> Collect Fees
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => openPrintReceipt(selectedStudent)} className="flex-1 min-w-[120px]">
                <Receipt className="w-4 h-4 mr-1.5" /> Receipt
              </Button>
              <Button size="sm" variant="outline" onClick={() => openPrintIdCard(selectedStudent)} className="flex-1 min-w-[120px]">
                <CreditCard className="w-4 h-4 mr-1.5" /> ID Card
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ============ Collect Fee Quick Modal ============ */}
      <Dialog open={collectFeeOpen} onOpenChange={setCollectFeeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-emerald-600" /> Collect Fee — {selectedStudent?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-slate-50 rounded-lg p-2"><p className="text-[10px] text-slate-500 uppercase">Total</p><p className="font-bold text-slate-900 text-sm">₹{(selectedStudent.total_fee || selectedStudent.course_fee || 0).toLocaleString()}</p></div>
                <div className="bg-emerald-50 rounded-lg p-2"><p className="text-[10px] text-emerald-600 uppercase">Paid</p><p className="font-bold text-emerald-700 text-sm">₹{(selectedStudent.fee_paid || 0).toLocaleString()}</p></div>
                <div className="bg-rose-50 rounded-lg p-2"><p className="text-[10px] text-rose-600 uppercase">Pending</p><p className="font-bold text-rose-700 text-sm">₹{(selectedStudent.fee_pending || 0).toLocaleString()}</p></div>
              </div>
              {[1, 2].map(n => {
                const amt = Number(selectedStudent[`installment_${n}_amount`]) || 0;
                if (amt <= 0) return null;
                const paid = Number(selectedStudent[`installment_${n}_paid_amount`]) || 0;
                const remaining = Math.max(0, amt - paid);
                const status = selectedStudent[`installment_${n}_status`] || 'pending';
                return (
                  <div key={n} className={`border rounded-lg p-2.5 flex items-center justify-between ${status === 'paid' ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-slate-200'}`}>
                    <div className="text-xs">
                      <p className="font-semibold">{n === 1 ? '1st' : '2nd'} Installment <span className="text-slate-400">· ₹{amt.toLocaleString()}</span></p>
                      <p className="text-[10px] text-slate-500">Paid: ₹{paid.toLocaleString()} · Remaining: ₹{remaining.toLocaleString()}</p>
                    </div>
                    {remaining > 0 && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setCollectAmount(remaining.toString())}>
                        Pay ₹{remaining.toLocaleString()}
                      </Button>
                    )}
                  </div>
                );
              })}
              <div>
                <Label>Amount</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">₹</span>
                  <Input type="number" value={collectAmount} onChange={e => setCollectAmount(e.target.value)} max={selectedStudent.fee_pending} className="pl-8 text-lg" data-testid="quick-collect-amount" />
                </div>
              </div>
              <div>
                <Label>Payment Mode</Label>
                <select value={collectMode} onChange={e => setCollectMode(e.target.value)} className="w-full h-10 px-3 mt-1 border border-slate-200 rounded-lg bg-white text-sm">
                  <option>Cash</option>
                  <option>UPI</option>
                  <option>Bank Transfer</option>
                  <option>Cheque</option>
                  <option>Card</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setCollectFeeOpen(false)} className="flex-1">Cancel</Button>
                <Button onClick={submitCollectFee} disabled={collectBusy} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-collect-fee">
                  {collectBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Collect ₹{collectAmount ? Number(collectAmount).toLocaleString() : '0'}</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CenterStudentsPage;
